import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import { useTranslationReady } from '../../hooks/useTranslationReady';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { PlusCircle, Edit, X, Check, Trash2, Brain, Paperclip, Download, Eye, Grid, List, File, LayoutGrid, Filter, ZoomIn, ZoomOut, AlertCircle, CheckCircle, FileText } from 'lucide-react';
import {
  getEntityColors,
  getEntityIcon,
  ENTITY_RING_CLASSES
} from '../../components/EntityManagement/EntityRelationshipCard';
import EntitySelectionModal from '../../components/EntityManagement/EntitySelectionModal';
import TagService from '../../components/Tags/TagService';
import SpeechToText from '../../components/Speech/SpeechToText';
import toast from 'react-hot-toast';
import entityRelationService from '../../services/entityRelationService';
import { getEntityRoute } from './noteAnalysisService';
import { handleGenerateSummary as generateSummary } from './createSummaryService';
import { STICKER_MARKER, parseStickerPayload, StickerPreview } from './stickerUtils';
import {
  decodeFileName,
  formatFileSize,
  isImageFile,
  getFileTypeIcon,
  parseAttachmentTags,
  fetchAttachments as fetchAttachmentsService,
  handleAttachmentUpload as handleAttachmentUploadService,
  handleDownloadAttachment as handleDownloadAttachmentService,
  handleDeleteAttachment as handleDeleteAttachmentService,
  loadImageAsBlob as loadImageAsBlobService,
  handleViewAttachment as handleViewAttachmentService,
  handleCloseViewAttachment as handleCloseViewAttachmentService
} from './attachmentService';
import EmailHoverPopover from '../../components/EmailHoverPopover/EmailHoverPopover';

const Notes = () => {
  const { user } = useAuth();
  // Check selected role first, then fall back to all roles
  const selectedRole = user?.selectedRole?.RoleName || user?.selectedRole;
  const hasEditRole = selectedRole 
    ? ['Salesperson', 'Manager', 'Admin'].includes(selectedRole)
    : user?.roles?.some(role => ['Salesperson', 'Manager', 'Admin'].includes(role.RoleName));
  const { t, i18n, ready, translationsReady, safeT } = useTranslationReady([
    'common.tags',
    'common.save',
    'common.cancel',
    'common.edit',
    'common.delete',
    'common.show',
    'common.hide',
    'common.to',
    'common.change',
    'Notes.relatedEntities',
    'Notes.pagetitle',
    'Notes.myNotes',
    'Notes.allNotes',
    'Notes.allUsers',
    'Notes.searchPlaceholder',
    'Notes.filterByTag',
    'Notes.allTags',
    'Notes.timeFilter',
    'Notes.timeFilterAll',
    'Notes.timeFilterToday',
    'Notes.timeFilterThisWeek',
    'Notes.timeFilterThisMonth',
    'Notes.timeFilterSelectMonth',
    'Notes.chooseMonth',
    'Notes.note',
    'Notes.notes',
    'Notes.filteredFrom',
    'Notes.total',
    'Notes.createSummary',
    'Notes.aiPowered',
    'Notes.addNote',
    'Notes.createNew',
    'Notes.loading',
    'Notes.attachment',
    'Notes.attachments',
    'Notes.addAttachments',
    'Notes.aiAnalysis',
    'Notes.noSearchResults',
    'Notes.clearSearch',
    'Notes.noNotes',
    'Notes.tagsPlaceholder',
    'Notes.addNewNote',
    'Notes.noteText',
    'Notes.notePlaceholder',
    'activities.entityTypes.customer',
    'activities.entityTypes.lead',
    'activities.entityTypes.opportunity',
    'activities.entityTypes.activity'
  ]);
  const { getProtectedTenantCode } = useTenantProtection();
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  
  // Abort ongoing network calls when tenant is switching to prevent request starvation
  const inFlightControllersRef = useRef(new Set());
  const isTenantSwitchingRef = useRef(false);
  
  const makeFetch = useCallback(async (url, options = {}) => {
    const controller = new AbortController();
    inFlightControllersRef.current.add(controller);
    try {
      const merged = { ...options, signal: controller.signal };
      return await fetch(url, merged);
    } finally {
      setTimeout(() => inFlightControllersRef.current.delete(controller), 0);
    }
  }, []);
  
  useEffect(() => {
    const onBefore = () => {
      isTenantSwitchingRef.current = true;
      inFlightControllersRef.current.forEach(ctrl => { try { ctrl.abort(); } catch (_) {} });
      inFlightControllersRef.current.clear();
    };
    const onAfter = () => { isTenantSwitchingRef.current = false; };
    window.addEventListener('tenant:beforeSwitch', onBefore);
    window.addEventListener('tenant:afterSwitch', onAfter);
    return () => {
      window.removeEventListener('tenant:beforeSwitch', onBefore);
      window.removeEventListener('tenant:afterSwitch', onAfter);
    };
  }, []);
  

const resolveUserId = (user) => {
  console.log('🆔 === RESOLVING USER ID ===');
  console.log('🆔 Full user object:', JSON.stringify(user, null, 2));
  
  if (!user) {
    console.warn('⚠️ USERID_DEBUG: User object is null/undefined');
    return null;
  }

  // CRITICAL: The backend uses req.user.tenantUserId for all tenant-specific operations
  // So we MUST prioritize tenantUserId over userId to ensure consistency
  // userId might be a global user ID in some contexts, so it should be a fallback only
  
  const candidates = [
    { name: 'tenantUserId', value: user.tenantUserId }, // PRIMARY: Explicit tenant user ID (used by backend)
    { name: 'UserID', value: user.UserID },           // FALLBACK 1: Database field name
    { name: 'userId', value: user.userId },           // FALLBACK 2: Could be global or tenant depending on context
    { name: 'userID', value: user.userID },           // FALLBACK 3: Alternative casing
    { name: 'UserId', value: user.UserId }            // FALLBACK 4: Alternative casing
  ];

  console.log('🆔 Checking candidates:', candidates.map(c => `${c.name}=${c.value}`).join(', '));

  for (const candidate of candidates) {
    if (candidate.value !== undefined && candidate.value !== null) {
      const trimmed = String(candidate.value).trim();
      if (trimmed !== '') {
        console.log('✅ USERID_DEBUG: Resolved user ID:', trimmed, 'from field:', candidate.name);
        return trimmed;
      }
    }
  }

  console.warn('⚠️ USERID_DEBUG: Failed to resolve user ID from user object');
  console.warn('⚠️ Available keys in user object:', Object.keys(user));
  return null;
};

  // Entity selection modal helpers
  const getEntityId = (entity, entityType) => {
    switch (entityType) {
      case 'Customer': return entity.CustomerID;
      case 'Lead': return entity.LeadID;
      case 'Opportunity': return entity.OpportunityID;
      case 'Activity': return entity.ActivityID;
      default: return null;
    }
  };

  const getEntityName = (entity, entityType) => {
    switch (entityType) {
      case 'Customer': {
        const personName = (entity.CustomerName && entity.CustomerName.trim()) || `${(entity.FirstName || '').trim()} ${(entity.LastName || '').trim()}`.trim();
        return personName || entity.CompanyName || `Customer ${entity.CustomerID}`;
      }
      case 'Lead': return entity.Title || entity.ContactName || entity.CompanyName || `Lead ${entity.LeadID}`;
      case 'Opportunity': return entity.OpportunityName || entity.Title || `Opportunity ${entity.OpportunityID}`;
      case 'Activity': return entity.Subject || `${entity.ActivityType || 'Activity'} ${entity.ActivityID}`;
      default: return `${entityType}`;
    }
  };

  const extractCustomerType = (entity) => {
    if (!entity) return undefined;
    const raw = entity.CustomerType || entity.customerType || entity.Type || entity.type || entity.CustomerCategory || entity.customerCategory;
    if (!raw) return undefined;
    const normalized = String(raw).toLowerCase();
    if (normalized.includes('person') || normalized.includes('individual')) {
      return 'Person';
    }
    if (normalized.includes('company') || normalized.includes('business') || normalized.includes('organisation') || normalized.includes('organization')) {
      return 'Company';
    }
    return undefined;
  };

  const extractCustomerCompanyId = (entity, customerType) => {
    if (!entity) return undefined;
    const keys = [
      'PersonCompanyID',
      'PersonCompanyId',
      'CompanyID',
      'CompanyId',
      'CompanyCustomerID',
      'CompanyCustomerId',
      'ParentCustomerID',
      'ParentCustomerId',
      'PrimaryCompanyID',
      'PrimaryCompanyId',
      'LinkedCompanyID',
      'LinkedCompanyId'
    ];
    for (const key of keys) {
      if (Object.prototype.hasOwnProperty.call(entity, key)) {
        const value = entity[key];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          const trimmed = String(value).trim();
          const numeric = Number(trimmed);
          return Number.isFinite(numeric) ? String(numeric) : trimmed;
        }
      }
    }
    // Some APIs nest under different casing
    if (customerType && customerType.toLowerCase() === 'person') {
      if (entity.Company) {
        return extractCustomerCompanyId(entity.Company, 'Company');
      }
    }
    if (customerType && customerType.toLowerCase() === 'company') {
      const fallback = entity.CustomerID || entity.CustomerId || entity.Id || entity.ID;
      if (fallback !== undefined && fallback !== null && String(fallback).trim() !== '') {
        const trimmed = String(fallback).trim();
        const numeric = Number(trimmed);
        return Number.isFinite(numeric) ? String(numeric) : trimmed;
      }
    }
    return undefined;
  };

  const extractCustomerCompanyName = (entity, customerType = extractCustomerType(entity)) => {
    if (!entity) return undefined;
    const resolvedType = customerType || extractCustomerType(entity);
    const nameCandidates = resolvedType === 'Person'
      ? [
          entity.PersonCompanyName,
          entity.PersonCompanyName_Eng,
          entity.CompanyName,
          entity.CompanyName_Eng,
          entity?.Company?.CompanyName,
          entity?.Company?.CompanyName_Eng,
          entity.CustomerName,
          entity.CustomerName_Eng,
          entity.CompanyCustomerName,
          entity.CompanyCustomerName_Eng,
          entity.OrganizationName,
          entity.OrgName,
          entity.AccountName
        ]
      : [
          entity.CompanyName,
          entity.CompanyName_Eng,
          entity.CustomerName,
          entity.CustomerName_Eng,
          entity.OrganizationName,
          entity.OrgName,
          entity.AccountName,
          entity.Name
        ];
    const found = nameCandidates.find(val => typeof val === 'string' && val.trim() !== '');
    return found ? found.trim() : undefined;
  };

  const attachCustomerMeta = (base, entityType, source) => {
    if (entityType !== 'Customer') return base;
    const customerType = extractCustomerType(source) || base.customerType;
    const companyName = extractCustomerCompanyName(source, customerType) || base.companyName;
    const companyId = extractCustomerCompanyId(source, customerType) || base.companyId || ((customerType || '').toLowerCase() === 'company' ? String(base.id) : undefined);
    return {
      ...base,
      ...(customerType ? { customerType } : {}),
      ...(companyName ? { companyName } : {}),
      ...(companyId ? { companyId } : {})
    };
  };

  const getCompanyKey = (entity) => {
    if (!entity || entity.type !== 'Customer') return null;
    if (entity.companyId) return `id:${entity.companyId}`;
    if (entity.companyName) return `name:${entity.companyName.toLowerCase()}`;
    return null;
  };

  const sortCustomersForDisplay = (customers) => {
    if (!Array.isArray(customers) || customers.length <= 1) return customers;
    const companies = [];
    const personsByKey = new Map();
    const orphans = [];

    customers.forEach((cust, index) => {
      const type = (cust.customerType || '').toLowerCase();
      if (type === 'company') {
        const key = getCompanyKey(cust) || `company:${cust.id ?? index}`;
        companies.push({ key, item: cust, index });
        if (!personsByKey.has(key)) personsByKey.set(key, []);
      } else if (type === 'person') {
        const key = getCompanyKey(cust);
        if (key) {
          if (!personsByKey.has(key)) personsByKey.set(key, []);
          personsByKey.get(key).push({ item: cust, index });
        } else {
          orphans.push({ item: cust, index });
        }
      } else {
        orphans.push({ item: cust, index });
      }
    });

    companies.sort((a, b) => a.index - b.index);
    personsByKey.forEach(list => list.sort((a, b) => a.index - b.index));
    orphans.sort((a, b) => a.index - b.index);

    const ordered = [];
    const used = new Set();

    companies.forEach(({ key, item }) => {
      ordered.push(item);
      used.add(item);
      const persons = personsByKey.get(key);
      if (persons) {
        persons.forEach(({ item: person }) => {
          if (!used.has(person)) {
            ordered.push(person);
            used.add(person);
          }
        });
        personsByKey.delete(key);
      }
    });

    const remainingPersons = [
      ...Array.from(personsByKey.values()).flat(),
      ...orphans
    ];
    remainingPersons.sort((a, b) => a.index - b.index);
    remainingPersons.forEach(({ item }) => {
      if (!used.has(item)) {
        ordered.push(item);
        used.add(item);
      }
    });

    customers.forEach(item => {
      if (!used.has(item)) ordered.push(item);
    });

    return ordered;
  };

  const mergeSortedCustomerOrder = (entities) => {
    if (!Array.isArray(entities)) return entities;
    const customers = entities.filter(e => e.type === 'Customer');
    if (customers.length <= 1) return entities;
    const sortedCustomers = sortCustomersForDisplay(customers);
    const unchanged = sortedCustomers.length === customers.length && sortedCustomers.every((cust, idx) => cust === customers[idx]);
    if (unchanged) return entities;
    let customerIndex = 0;
    return entities.map(item => {
      if (item.type !== 'Customer') return item;
      return sortedCustomers[customerIndex++] || item;
    });
  };


  const performEntitySearchForModal = async (term, entityType) => {
    try {
      let endpoint = '';
      const tenantCode = getProtectedTenantCode();
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
      };

      switch (entityType) {
        case 'Customer':
          if (!term || term.trim() === '') {
            endpoint = `/api/customers?limit=10&_ts=${Date.now()}`;
          } else {
            endpoint = `/api/customers?search=${encodeURIComponent(term)}&limit=15&_ts=${Date.now()}`;
          }
          break;
        case 'Lead':
          if (!term || term.trim() === '') {
            endpoint = `/api/leads/for-assignment?limit=10&_ts=${Date.now()}`;
          } else {
            endpoint = `/api/leads/for-assignment?search=${encodeURIComponent(term)}&limit=15&_ts=${Date.now()}`;
          }
          break;
        case 'Opportunity':
          if (!term || term.trim() === '') {
            endpoint = `/api/opportunities/for-assignment?limit=10&_ts=${Date.now()}`;
          } else {
            endpoint = `/api/opportunities/for-assignment?search=${encodeURIComponent(term)}&limit=15&_ts=${Date.now()}`;
          }
          break;
        case 'Activity':
          if (!term || term.trim() === '') {
            endpoint = `/api/activities?limit=10&_ts=${Date.now()}`;
          } else {
            endpoint = `/api/activities?search=${encodeURIComponent(term)}&limit=15&_ts=${Date.now()}`;
          }
          break;
        default:
          console.warn(`Unsupported entity type for search: ${entityType}`);
          setEntitySelectionModal(prev => ({ ...prev, results: [], loading: false }));
          return;
      }

      console.log(`Searching ${entityType} for modal with endpoint:`, endpoint);

      const response = await fetch(endpoint, { headers });

      console.log(`${entityType} modal search response status:`, response.status);

      if (response.ok) {
        const data = await response.json();
        console.log(`${entityType} modal search response:`, data);
        
        // Handle different response structures
        let results = [];
        const entityKey = entityType.toLowerCase() + 's';
        if (data[entityKey]) {
          results = data[entityKey];
        } else if (data.customers && Array.isArray(data.customers)) {
          results = data.customers;
        } else if (data.leads && Array.isArray(data.leads)) {
          results = data.leads;
        } else if (data.opportunities && Array.isArray(data.opportunities)) {
          results = data.opportunities;
        } else if (data.activities && Array.isArray(data.activities)) {
          results = data.activities;
        } else if (data.data && Array.isArray(data.data)) {
          results = data.data;
        } else if (Array.isArray(data)) {
          results = data;
        }
        
        console.log(`${entityType} modal search results:`, results);
        
        setEntitySelectionModal(prev => ({
          ...prev,
          results,
          loading: false
        }));
      } else {
        console.error(`${entityType} modal search failed:`, response.status, response.statusText);
        // Try to get error details
        try {
          const errorData = await response.json();
          console.error('Error details:', errorData);
        } catch (e) {
          console.error('Could not parse error response');
        }
        setEntitySelectionModal(prev => ({
          ...prev,
          results: [],
          loading: false
        }));
      }
    } catch (err) {
      console.error(`${entityType} modal search failed:`, err);
      setEntitySelectionModal(prev => ({
        ...prev,
        results: [],
        loading: false
      }));
    }
  };

  const toggleEntitySelectionInModal = (entity, entityTypeOverride) => {
    const entityType = entityTypeOverride || entitySelectionModal.entityType;
    const entityId = getEntityId(entity, entityType);
    const entityIdStr = String(entityId);
    console.log('Toggle entity selection:', { entity, entityId, entityIdStr, entityType, currentSelectedIds: entitySelectionModal.selectedIds });
    
    if (!entityId) {
      console.warn('Cannot toggle selection: entity ID is null/undefined', entity);
      return;
    }
    
    setEntitySelectionModal(prev => {
      const newSelectedIds = prev.selectedIds.includes(entityIdStr)
        ? prev.selectedIds.filter(id => id !== entityIdStr)
        : [...prev.selectedIds, entityIdStr];
      
      console.log('Updated selected IDs:', newSelectedIds);
      
      return {
        ...prev,
        selectedIds: newSelectedIds
      };
    });
  };
  const handleEntityModalSearchChange = (value) => {
    setEntitySelectionModal(prev => ({ ...prev, searchTerm: value }));
    if (searchTimeouts.modal) clearTimeout(searchTimeouts.modal);
    const entityType = entitySelectionModal.entityType;
    if (!entityType) return;
    const timeoutId = setTimeout(() => performEntitySearchForModal(value, entityType), 300);
    setSearchTimeouts(prev => ({ ...prev, modal: timeoutId }));
  };


  const closeEntitySelectionModal = async (addSelected) => {
    // Close modal immediately for better UX; continue work in background
    const ctx = entitySelectionModal;
    setEntitySelectionModal({ 
      isOpen: false, 
      entityType: null, 
      searchTerm: '', 
      results: [], 
      loading: false, 
      selectedIds: [], 
      context: null 
    });

    if (addSelected && ctx.entityType) {
      const entityType = ctx.entityType;
      const selectedSet = new Set(ctx.selectedIds);
      
      if (entitySelectionModal.context === 'newNote') {
        // Build new list for new note
        const newEntities = ctx.results
          .filter(ent => selectedSet.has(String(getEntityId(ent, entityType))))
          .map(ent => {
            const id = getEntityId(ent, entityType);
            const name = getEntityName(ent, entityType);
            const base = { type: entityType, id, name };
            return attachCustomerMeta(base, entityType, ent);
          });
        // Remove any deselected of this type, then add
        setNewNoteRelatedEntities(prev => {
          const others = prev.filter(e => e.type !== entityType);
          const combined = [...others, ...newEntities];
          return mergeSortedCustomerOrder(combined);
        });
        if (!newNoteSelectedEntityTypes.includes(entityType)) {
          setNewNoteSelectedEntityTypes(prev => [...prev, entityType]);
        }
      } else if (entitySelectionModal.context && entitySelectionModal.context.editingNoteId) {
        // Handle editing note - update editing state, will be saved when note is saved
        const newEntities = ctx.results
          .filter(ent => selectedSet.has(String(getEntityId(ent, entityType))))
          .map(ent => {
            const id = getEntityId(ent, entityType);
            const name = getEntityName(ent, entityType);
            const base = { type: entityType, id, name };
            return attachCustomerMeta(base, entityType, ent);
          });
        // Remove any deselected of this type, then add
        setEditingNoteRelatedEntities(prev => {
          const others = prev.filter(e => e.type !== entityType);
          const combined = [...others, ...newEntities];
          return mergeSortedCustomerOrder(combined);
        });
        if (!editingNoteSelectedEntityTypes.includes(entityType)) {
          setEditingNoteSelectedEntityTypes(prev => [...prev, entityType]);
        }
      } else if (ctx.context === 'summary') {
        // Handle summary entity selection (single entity only)
        if (selectedSet.size > 0) {
          const selectedEntity = ctx.results.find(ent => selectedSet.has(String(getEntityId(ent, entityType))));
          if (selectedEntity) {
            const entityId = getEntityId(selectedEntity, entityType);
            const entityName = getEntityName(selectedEntity, entityType);
            console.log('Selected entity for summary:', { entityType, entityId, entityName });
            setSummaryOptions(prev => ({
              ...prev,
              entityId: String(entityId),
              entityName: entityName
            }));
          }
        }
      } else if (ctx.context && ctx.context.noteId) {
        const noteId = ctx.context.noteId;
        const current = noteRelations[noteId] || [];
        const selectedEntities = ctx.results
          .filter(ent => selectedSet.has(String(getEntityId(ent, entityType))))
          .map(ent => {
            const id = getEntityId(ent, entityType);
            const name = getEntityName(ent, entityType);
            const base = { type: entityType, id, name };
            return attachCustomerMeta(base, entityType, ent);
          });
        // Merge with other types unchanged
        const merged = [
          ...current.filter(e => e.type !== entityType),
          ...selectedEntities
        ];
        // Optimistic UI: reflect new relations instantly
        setNoteRelations(prev => ({ ...prev, [noteId]: mergeSortedCustomerOrder(merged) }));
        // Compute diffs against existing relations
        const existingRaw = noteRelationsRaw[noteId] || [];
        const existingKeys = new Set(existingRaw.map(rel => {
          const t = rel.SourceEntityType === 'SaleNote' ? rel.TargetEntityType : rel.SourceEntityType;
          const i = rel.SourceEntityType === 'SaleNote' ? rel.TargetEntityID : rel.SourceEntityID;
          return `${t}:${i}`;
        }));
        const selectedKeys = new Set(merged.map(e => `${e.type}:${e.id}`));
        const toCreate = [...selectedKeys].filter(k => !existingKeys.has(k)).map(k => {
          const [t, i] = k.split(':');
          return { type: t, id: Number(i) };
        });
        const toDelete = existingRaw.filter(rel => {
          const t = rel.SourceEntityType === 'SaleNote' ? rel.TargetEntityType : rel.SourceEntityType;
          const i = rel.SourceEntityType === 'SaleNote' ? rel.TargetEntityID : rel.SourceEntityID;
          return !selectedKeys.has(`${t}:${i}`);
        });
        try {
          Promise.allSettled([
            ...toCreate.map(ent => entityRelationService.create({
              sourceEntityType: 'SaleNote',
              sourceEntityID: noteId,
              targetEntityType: ent.type,
              targetEntityID: ent.id,
              relationType: 'related_to'
            })),
            ...toDelete.map(rel => entityRelationService.delete(rel.RelationID))
          ]).finally(() => {
            fetchNoteRelations(noteId);
            try {
              toast.success(safeT('Notes.relationsUpdated', 'Relations updated'));
            } catch (_) {}
          });
        } catch (e) {
          console.warn('Relation sync failed for note', noteId, e);
          try {
            toast.error(safeT('Notes.failedToUpdateRelations', 'Failed to update relations'));
          } catch (_) {}
        }
      }
    }
  };

  // Open modal helpers
  const openEntitySelectionForNewNote = (entityType) => {
    const alreadySelectedIds = newNoteRelatedEntities.filter(e => e.type === entityType).map(e => String(e.id));
    console.log('Opening entity selection modal for new note:', { entityType, alreadySelectedIds });
    setEntitySelectionModal({
      isOpen: true,
      entityType,
      searchTerm: '',
      results: [],
      loading: true,
      selectedIds: alreadySelectedIds,
      context: 'newNote'
    });
    performEntitySearchForModal('', entityType);
  };


  const openEntitySelectionForEditingNote = (entityType) => {
    const alreadySelectedIds = editingNoteRelatedEntities.filter(e => e.type === entityType).map(e => String(e.id));
    console.log('Opening entity selection modal for editing note:', { entityType, alreadySelectedIds, editingNote });
    setEntitySelectionModal({
      isOpen: true,
      entityType,
      searchTerm: '',
      results: [],
      loading: true,
      selectedIds: alreadySelectedIds,
      context: { editingNoteId: editingNote }
    });
    performEntitySearchForModal('', entityType);
  };

  const openEntitySelectionForSummary = (entityType) => {
    const alreadySelectedIds = summaryOptions.entityId ? [String(summaryOptions.entityId)] : [];
    console.log('Opening entity selection modal for summary:', { entityType, alreadySelectedIds });
    setEntitySelectionModal({
      isOpen: true,
      entityType,
      searchTerm: '',
      results: [],
      loading: true,
      selectedIds: alreadySelectedIds,
      context: 'summary'
    });
    performEntitySearchForModal('', entityType);
  };



  const [notes, setNotes] = useState([]);
  const [notesPage, setNotesPage] = useState(1);
  const [notesPerPage, setNotesPerPage] = useState(() => {
    try {
      if (typeof window === 'undefined') return 20;
      const stored = localStorage.getItem('notesPerPage');
      const parsed = parseInt(stored, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 20;
    } catch (error) {
      return 20;
    }
  });
  const [hasMoreNotes, setHasMoreNotes] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('Conversation');
  const [tags, setTags] = useState('');
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [newNoteAttachmentFiles, setNewNoteAttachmentFiles] = useState([]);
  const [viewMode, setViewMode] = useState('my'); // 'my' or 'all'
  const [displayMode, setDisplayMode] = useState('list'); // 'card' or 'list' - for UI layout
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  
  // Selection state for bulk actions
  const [selectedNotes, setSelectedNotes] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // Email lookup cache to avoid repeated API calls
  const [emailLookupCache, setEmailLookupCache] = useState(new Map());
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [editNoteType, setEditNoteType] = useState('');
  const [editTags, setEditTags] = useState('');
  const [editingNoteRelatedEntities, setEditingNoteRelatedEntities] = useState([]);
  const [editingNoteSelectedEntityTypes, setEditingNoteSelectedEntityTypes] = useState([]);
  const [editingNoteAttachments, setEditingNoteAttachments] = useState([]);
  const [editingNoteAttachmentFiles, setEditingNoteAttachmentFiles] = useState([]);
  const [deletingNote, setDeletingNote] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all'); // all, today, thisWeek, thisMonth, month
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedTagFilter, setSelectedTagFilter] = useState('');
  const [availableTags, setAvailableTags] = useState([]);
  const [activeTypeTab, setActiveTypeTab] = useState('all'); // Active tab for note types
  const [typeCounts, setTypeCounts] = useState({}); // Counts for each type tab
  const [availableTypes, setAvailableTypes] = useState([]);
  const [focusedNoteId, setFocusedNoteId] = useState(null);
  const [hasScrolledToNote] = useState(false);
  const [lastOpenedNoteIdFromQuery, setLastOpenedNoteIdFromQuery] = useState(null);
  const noteRefs = useRef({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [attachments, setAttachments] = useState({});
  const [uploadingAttachments, setUploadingAttachments] = useState({});
  const [deletingAttachments, setDeletingAttachments] = useState({});
  
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [selectedNoteForAttachment, setSelectedNoteForAttachment] = useState(null);
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [showViewAttachmentModal, setShowViewAttachmentModal] = useState(false);
  const [selectedAttachmentForView, setSelectedAttachmentForView] = useState(null);
  const [viewingAttachment, setViewingAttachment] = useState(false);
  const [attachmentViewUrl, setAttachmentViewUrl] = useState(null);
  const [textContentModalOpen, setTextContentModalOpen] = useState(false);
  const [viewingTextContent, setViewingTextContent] = useState(null);
  const [modalSize, setModalSize] = useState({ width: '90%', height: '85%' });
  const [isResizing, setIsResizing] = useState(false);
  // Track image loading errors for gallery view
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  // Track blob URLs for gallery view images
  const [imageBlobUrls, setImageBlobUrls] = useState({});
  // Track which images are currently loading
  const [imageLoading, setImageLoading] = useState({});
  // Removed analyzed notes UI state
  // AI Analysis hook removed - feature under construction
  // Relations state
  const [noteRelations, setNoteRelations] = useState({});
  const [noteRelationsRaw, setNoteRelationsRaw] = useState({});
  // New Note related entities selection
  const [newNoteSelectedEntityTypes, setNewNoteSelectedEntityTypes] = useState([]);
  const [newNoteRelatedEntities, setNewNoteRelatedEntities] = useState([]);
  // Shared entity selection modal state
  const [entitySelectionModal, setEntitySelectionModal] = useState({
    isOpen: false,
    entityType: null,
    searchTerm: '',
    results: [],
    loading: false,
    selectedIds: [],
    context: null // 'newNote' or { noteId }
  });
  const [searchTimeouts, setSearchTimeouts] = useState({});
  // UI: per-note collapse state for related entities
  const [relationsCollapsed, setRelationsCollapsed] = useState({});

  const toggleRelationsCollapse = (noteId) => {
    setRelationsCollapsed(prev => ({ ...prev, [noteId]: !prev[noteId] }));
  };

  // UI: per-note expand/collapse state for long notes
  const [expandedNotes, setExpandedNotes] = useState({});
  
  const toggleNoteExpansion = (noteId) => {
    setExpandedNotes(prev => {
      const willExpand = !prev[noteId];
      if (willExpand) {
        if (!noteRelations[noteId]) {
          fetchNoteRelations(noteId);
        }
        if (!attachments[noteId]) {
          fetchAttachments(noteId);
        }
      }
      return { ...prev, [noteId]: willExpand };
    });
  };

  // UI: per-note attachment view mode (list vs gallery)
  const [attachmentViewMode, setAttachmentViewMode] = useState({});
  
  // Single note view modal state
  const [showNoteViewModal, setShowNoteViewModal] = useState(false);
  const [selectedNoteForView, setSelectedNoteForView] = useState(null);
  const [showAttachmentsInView, setShowAttachmentsInView] = useState(true);
  const [noteContentFontSize, setNoteContentFontSize] = useState(16); // Default font size in px
  
  // Summary feature state
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryOptions, setSummaryOptions] = useState({
    style: 'focus_points',
    filterBy: 'none', // none, tag, date, entity
    filterValue: '',
    scope: 'my', // my, all
    dateFrom: '',
    dateTo: '',
    datePreset: '', // today, thisweek, lastweek, thismonth, lastmonth, custom
    entityType: '',
    entityId: '',
    entityName: '', // Store entity name for display
    language: 'th', // th, en
    noteTypes: [], // Array of selected note types
    includeAttachments: false, // Whether to include attachment content in summary
    additionalInstructions: '' // Additional instructions for the AI
  });
  const [tagFilterMode, setTagFilterMode] = useState('popular'); // 'popular' or 'all'
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [tagFrequencyMap, setTagFrequencyMap] = useState({});
  const [availableNoteTypes, setAvailableNoteTypes] = useState([]);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [summaryProgress, setSummaryProgress] = useState({
    step: '',
    current: 0,
    total: 0,
    message: ''
  });
  const [ocrAvailable, setOcrAvailable] = useState(true); // Track OCR service availability
  
  const toggleAttachmentViewMode = (noteId, mode) => {
    setAttachmentViewMode(prev => ({ 
      ...prev, 
      [noteId]: mode 
    }));
    
    // Load images when switching to gallery view
    if (mode === 'gallery' && attachments[noteId]) {
      attachments[noteId].forEach(attachment => {
        const isImage = attachment.MimeType && attachment.MimeType.toLowerCase().startsWith('image/');
        const attachmentId = attachment.AttachmentID;
        if (isImage && 
            !imageBlobUrls[attachmentId] && 
            !imageLoadErrors[attachmentId] && 
            !imageLoading[attachmentId]) {
          loadImageAsBlob(attachmentId);
        }
      });
    }
    
    // Clean up blob URLs when switching away from gallery view (optional - can keep for performance)
    // Uncomment if you want to free memory when switching to list view
    // if (prevMode === 'gallery' && mode !== 'gallery' && attachments[noteId]) {
    //   attachments[noteId].forEach(attachment => {
    //     const attachmentId = attachment.AttachmentID;
    //     if (imageBlobUrls[attachmentId]) {
    //       window.URL.revokeObjectURL(imageBlobUrls[attachmentId]);
    //       setImageBlobUrls(prev => {
    //         const newState = { ...prev };
    //         delete newState[attachmentId];
    //         return newState;
    //       });
    //     }
    //   });
    // }
  };

  // Helper to truncate note text
  const truncateNote = (noteText, maxLength = 500) => {
    if (!noteText || noteText.length <= maxLength) return noteText;
    
    // Find a good breaking point (end of sentence or word)
    let truncated = noteText.substring(0, maxLength);
    const lastPeriod = truncated.lastIndexOf('.');
    const lastSpace = truncated.lastIndexOf(' ');
    
    if (lastPeriod > maxLength * 0.7) {
      truncated = noteText.substring(0, lastPeriod + 1);
    } else if (lastSpace > maxLength * 0.7) {
      truncated = noteText.substring(0, lastSpace);
    }
    
    return truncated;
  };

  // Helper to check if note is long (more than 500 characters)
  // If remaining content after truncation is less than 500 characters, show all
  const isNoteLong = (noteText) => {
    if (!noteText || noteText.length <= 500) return false;
    
    // Truncate the note to see how much would be shown
    const truncated = truncateNote(noteText, 500);
    const remainingLength = noteText.length - truncated.length;
    
    // If remaining content is less than 500 characters, show all instead of truncating
    if (remainingLength < 500) {
      return false;
    }
    
    return true;
  };

const currentUserId = useMemo(() => resolveUserId(user), [user]);

const fetchNotes = useCallback(async () => {
  setLoading(true);
  try {
    if (viewMode === 'my' && !currentUserId) {
      setLoading(false);
      return;
    }
    console.log('=== FETCHING NOTES ===');
    console.log('View mode:', viewMode);
    console.log('Selected user ID:', selectedUserId);
    console.log('Current user ID (resolved):', currentUserId);
    console.log('User object for debugging:', {
      userId: user?.userId,
      tenantUserId: user?.tenantUserId,
      UserID: user?.UserID,
      email: user?.email,
      username: user?.username
    });

    let url = '/api/sales-notes';
    const params = new URLSearchParams();
    
    params.append('page', String(notesPage));
    params.append('limit', String(notesPerPage));

    if (viewMode === 'all' && selectedUserId) {
      console.log('Mode: All notes with specific user filter');
      params.append('userId', selectedUserId);
    } else if (viewMode === 'my' && currentUserId) {
      console.log('Mode: My notes only');
      params.append('userId', currentUserId);
    } else if (viewMode === 'all' && !selectedUserId) {
      console.log('Mode: All notes without filter');
    }

    url += `?${params.toString()}`;

    console.log('Final URL:', url);

    const tenantCode = getProtectedTenantCode();
    const selectedRole = localStorage.getItem('selectedRole');
    const headers = {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
      ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
    };
    
    console.log('🔍 NOTES_FETCH: Request headers:', {
      hasToken: !!headers['Authorization'],
      tenantCode: tenantCode || 'none',
      selectedRole: selectedRole || 'none'
    });
    
    const response = await makeFetch(url, { headers });
    console.log('Fetch response status:', response.status);
    console.log('Fetch response ok:', response.ok);

    if (response.ok) {
      const data = await response.json();
      console.log('Fetched data count:', data?.recordset?.length || data?.length || 0);
      // Extract notes from recordset if it exists, otherwise use data directly
      const notesArray = data.recordset ? data.recordset : (Array.isArray(data) ? data : []);
      console.log('Setting notes to array with', notesArray.length, 'items');
      setNotes(notesArray);
      setHasMoreNotes(notesArray.length === notesPerPage);
      setError(null); // Clear any previous errors
    } else if (response.status === 404) {
      // Handle 404 gracefully - set empty notes array
      console.log('No notes found (404)');
      setNotes([]);
      setHasMoreNotes(false);
      setError(null);
    } else {
      const errorData = await response.json();
      console.error('Fetch error response:', errorData);
      throw new Error('Failed to fetch notes');
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('fetchNotes aborted due to tenant switch');
    } else {
      console.error('Fetch error:', error);
      setError(error.message);
      setNotes([]); // Set empty array on error
      setHasMoreNotes(false);
    }
  } finally {
    setLoading(false);
  }
}, [viewMode, selectedUserId, currentUserId, getProtectedTenantCode, makeFetch, user, notesPage, notesPerPage]);

  const fetchAllUsers = useCallback(async () => {
    try {
      const tenantCode = getProtectedTenantCode();
      const response = await makeFetch('/api/users?forNotes=true', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Users data:', data);
        const users = data.users || [];
        setAllUsers(users);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('fetchAllUsers aborted due to tenant switch');
      } else {
        console.error('Error fetching users:', error);
      }
    }
  }, [getProtectedTenantCode, makeFetch]);

  // Helper: Load entity relations for a note
  const fetchNoteRelations = useCallback(async (noteId) => {
    try {
      const res = await entityRelationService.getRelatedEntities('SaleNote', noteId, { direction: 'both' });
      const relations = res?.relations || [];
      setNoteRelationsRaw(prev => ({ ...prev, [noteId]: relations }));
      const entities = relations.map(rel => {
        const entityType = rel.SourceEntityType === 'SaleNote' ? rel.TargetEntityType : rel.SourceEntityType;
        const entityId = rel.SourceEntityType === 'SaleNote' ? rel.TargetEntityID : rel.SourceEntityID;
        const base = {
          type: entityType,
          id: entityId,
          name: rel.TargetName || rel.SourceName || ''
        };
        const relationMeta = {
          CustomerType: rel.CustomerType || rel.TargetCustomerType || rel.SourceCustomerType,
          CompanyName: rel.CompanyName || rel.TargetCompanyName || rel.SourceCompanyName,
          CompanyName_Eng: rel.CompanyName_Eng || rel.TargetCompanyName_Eng || rel.SourceCompanyName_Eng,
          PersonCompanyName: rel.PersonCompanyName || rel.TargetPersonCompanyName || rel.SourcePersonCompanyName,
          PersonCompanyName_Eng: rel.PersonCompanyName_Eng || rel.TargetPersonCompanyName_Eng || rel.SourcePersonCompanyName_Eng,
          CompanyID: rel.CompanyID || rel.TargetCompanyID || rel.SourceCompanyID,
          CompanyCustomerID: rel.CompanyCustomerID || rel.TargetCompanyCustomerID || rel.SourceCompanyCustomerID,
          PersonCompanyID: rel.PersonCompanyID || rel.TargetPersonCompanyID || rel.SourcePersonCompanyID
        };
        return attachCustomerMeta(base, entityType, relationMeta);
      });
      // Enrich names where missing
      const toEnrich = entities.filter(e => !e.name);
      if (toEnrich.length > 0) {
        const enriched = await Promise.all(entities.map(async (e) => {
          if (e.name) return e;
          try {
            let detail;
            if (e.type === 'Customer') {
              const resp = await fetch(`/api/customers/${e.id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
              if (resp.ok) {
                detail = await resp.json();
                const cust = detail?.customer || detail || {};
                const name = cust.CustomerType === 'Person'
                  ? `${cust.FirstName || ''} ${cust.LastName || ''}`.trim() || cust.CustomerName
                  : cust.CompanyName || cust.CustomerName;
                const updated = {
                  ...e,
                  name: name || e.name
                };
                return attachCustomerMeta(updated, 'Customer', cust);
              }
            } else if (e.type === 'Lead') {
              const resp = await fetch(`/api/leads/${e.id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
              if (resp.ok) {
                detail = await resp.json();
                const ld = detail?.lead || detail || {};
                const name = ld.Title || ld.ContactName || ld.CompanyName;
                return { ...e, name: name || e.name };
              }
            } else if (e.type === 'Opportunity') {
              const resp = await fetch(`/api/opportunities/${e.id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
              if (resp.ok) {
                detail = await resp.json();
                const opp = detail?.opportunity || detail || {};
                const name = opp.OpportunityName || opp.CustomerName;
                return { ...e, name: name || e.name };
              }
            } else if (e.type === 'Activity') {
              const resp = await fetch(`/api/activities/${e.id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
              if (resp.ok) {
                detail = await resp.json();
                const act = detail?.activity || detail || {};
                const name = act.Subject || `${act.ActivityType || 'Activity'} ${e.id}`;
                return { ...e, name: name || e.name };
              }
            }
          } catch (_e) {
            // ignore enrichment failure
          }
          return e;
        }));
        setNoteRelations(prev => ({ ...prev, [noteId]: mergeSortedCustomerOrder(enriched) }));
      } else {
        setNoteRelations(prev => ({ ...prev, [noteId]: mergeSortedCustomerOrder(entities) }));
      }
    } catch (e) {
      console.warn('Failed to fetch relations for note', noteId, e);
    }
  }, []);

  // Attachment handling functions - wrappers for service functions
  const fetchAttachments = useCallback(async (noteId) => {
    await fetchAttachmentsService(noteId, getProtectedTenantCode, makeFetch, setAttachments);
  }, [getProtectedTenantCode, makeFetch]);

  useEffect(() => {
    console.log('=== LOAD DATA EFFECT TRIGGERED ===');
    console.log('User object:', user);
    console.log('User roles:', user?.roles);
    console.log('View mode:', viewMode);
    console.log('Selected user ID:', selectedUserId);
    
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchNotes();
        // Fetch users for all salespeople to enable "All Notes" functionality
        const canViewAllNotes = selectedRole 
          ? ['Salesperson', 'Manager', 'Admin'].includes(selectedRole)
          : user?.roles?.some(role => 
              role.RoleName === 'Salesperson' || 
              role.RoleName === 'Manager' || 
              role.RoleName === 'Admin'
            );
        if (canViewAllNotes) {
          await fetchAllUsers();
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [viewMode, selectedUserId, currentUserId, user?.roles, user?.selectedRole, fetchNotes, fetchAllUsers, user]);

  useEffect(() => {
    console.log('Notes state changed:', notes);
  }, [notes]);

  useEffect(() => {
    try {
      localStorage.setItem('notesPerPage', String(notesPerPage));
    } catch (error) {
      // Ignore persistence errors (e.g., private mode)
    }
  }, [notesPerPage]);

  useEffect(() => {
    setNotesPage(1);
  }, [viewMode, selectedUserId, notesPerPage]);

  // Time filter function - defined before use in type counts calculation
  const noteMatchesTimeFilter = useCallback((note) => {
    if (!note?.NoteDate) return false;
    const date = new Date(note.NoteDate);
    if (Number.isNaN(date.getTime())) return false;

    const now = new Date();
    switch (timeFilter) {
      case 'today': {
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const end = new Date(start);
        end.setDate(end.getDate() + 1);
        return date >= start && date < end;
      }
      case 'thisWeek': {
        const start = new Date(now);
        const day = start.getDay(); // 0 Sunday
        const diff = (day === 0 ? -6 : 1) - day; // Monday as first day
        start.setHours(0, 0, 0, 0);
        start.setDate(start.getDate() + diff);
        const end = new Date(start);
        end.setDate(end.getDate() + 7);
        return date >= start && date < end;
      }
      case 'thisMonth': {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        return date >= start && date < end;
      }
      case 'month': {
        if (!selectedMonth) return true;
        const [year, month] = selectedMonth.split('-').map(Number);
        if (!year || !month) return true;
        const start = new Date(year, month - 1, 1);
        const end = new Date(year, month, 1);
        return date >= start && date < end;
      }
      default:
        return true;
    }
  }, [timeFilter, selectedMonth]);

  // Extract available tags from notes
  useEffect(() => {
    const tagSet = new Set();
    notes.forEach(note => {
      if (note.Tags) {
        const tags = typeof note.Tags === 'string'
          ? note.Tags.split(',').map(t => t.trim()).filter(Boolean)
          : Array.isArray(note.Tags)
            ? note.Tags.map(t => String(t).trim()).filter(Boolean)
            : [];
        tags.forEach(tag => tagSet.add(tag));
      }
    });
    setAvailableTags(Array.from(tagSet).sort());
  }, [notes]);

  // Extract available types from notes and calculate counts
  // Counts should respect time filter, view mode, tag filter, and search but NOT type filter
  useEffect(() => {
    const typeSet = new Set();
    
    // Filter notes by all criteria except type
    let filtered = [...notes];
    
    // View mode filter (my/all)
    if (viewMode === 'my') {
      const currentUserId = user?.UserID ? String(user.UserID) : null;
      if (currentUserId) {
        filtered = filtered.filter(note => {
          const ownerId =
            note?.UserID ??
            note?.CreatedBy ??
            note?.CreatedByUserID ??
            note?.AuthorUserID ??
            note?.OwnerUserID ??
            note?.AssignedUserID;
          const ownerIdStr = ownerId ? String(ownerId) : null;
          return ownerIdStr === currentUserId;
        });
      }
    } else if (viewMode === 'all' && selectedUserId) {
      const targetUserId = String(selectedUserId);
      filtered = filtered.filter(note => {
        const ownerId =
          note?.UserID ??
          note?.CreatedBy ??
          note?.CreatedByUserID ??
          note?.AuthorUserID ??
          note?.OwnerUserID ??
          note?.AssignedUserID;
        const ownerIdStr = ownerId ? String(ownerId) : null;
        return ownerIdStr === targetUserId;
      });
    }
    
    // Time filter
    filtered = filtered.filter(note => noteMatchesTimeFilter(note));
    
    // Tag filter
    if (selectedTagFilter && selectedTagFilter.trim()) {
      filtered = filtered.filter(note => {
        if (!note.Tags) return false;
        const noteTags = typeof note.Tags === 'string' 
          ? note.Tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
          : Array.isArray(note.Tags) 
            ? note.Tags.map(t => String(t).trim().toLowerCase()).filter(Boolean)
            : [];
        return noteTags.includes(selectedTagFilter.toLowerCase());
      });
    }
    
    // Search filter
    if (searchTerm.trim()) {
      // Split search term by spaces to allow OR matching (email OR name)
      // This allows searching for "john@example.com John Doe" to match notes containing either email OR name
      const searchTerms = searchTerm.toLowerCase().trim().split(/\s+/).filter(term => term.length > 0);
      filtered = filtered.filter(note => {
        const noteTextLower = note.NoteText?.toLowerCase() || '';
        const tagsLower = note.Tags?.toLowerCase() || '';
        const typeLower = note.Type?.toLowerCase() || '';
        const authorName = `${note.FirstName || ''} ${note.LastName || ''}`.toLowerCase();
        
        // Check if ANY of the search terms match (OR logic)
        // This means a note will match if it contains the email OR the name
        return searchTerms.some(searchTerm => {
          return (
            noteTextLower.includes(searchTerm) ||
            tagsLower.includes(searchTerm) ||
            typeLower.includes(searchTerm) ||
            authorName.includes(searchTerm)
          );
        });
      });
    }
    
    // Now calculate counts from filtered notes
    const counts = { all: filtered.length };
    
    filtered.forEach(note => {
      if (note.Type && note.Type.trim()) {
        const type = note.Type.trim();
        typeSet.add(type);
        counts[type] = (counts[type] || 0) + 1;
      }
    });
    
    setAvailableTypes(Array.from(typeSet).sort());
    setTypeCounts(counts);
  }, [notes, noteMatchesTimeFilter, viewMode, user?.UserID, selectedUserId, selectedTagFilter, searchTerm, timeFilter, selectedMonth]);

  // Auto-show filter bar when filters are active
  useEffect(() => {
    if (searchTerm || selectedTagFilter || timeFilter !== 'all') {
      setShowFilters(true);
    }
  }, [searchTerm, selectedTagFilter, timeFilter]);

  // Note metadata (attachments/relations) is fetched lazily on demand.

  useEffect(() => {
    console.log('=== FILTERING NOTES ===');
    console.log('View mode:', viewMode);
    console.log('Current user ID (resolved):', currentUserId);
    console.log('Total notes before filtering:', notes.length);
    
    let filtered = notes;

    if (viewMode === 'my') {
      console.log('🔍 MY NOTES MODE - Filtering for current user:', currentUserId);
      if (!currentUserId) {
        console.warn('⚠️ No current user ID - showing empty list');
        filtered = [];
      } else {
        filtered = filtered.filter((note, index) => {
          const ownerId =
            note?.UserID ??
            note?.CreatedBy ??
            note?.CreatedByUserID ??
            note?.AuthorUserID ??
            note?.OwnerUserID ??
            note?.AssignedUserID;
          const ownerIdStr = ownerId ? String(ownerId) : null;
          const matches = ownerIdStr === currentUserId;
          
          // Debug log for first 5 notes and any that match
          if (index < 5 || matches) {
            console.log(`Note ${note.NoteID}:`, {
              UserID: note?.UserID,
              CreatedBy: note?.CreatedBy,
              CreatedByUserID: note?.CreatedByUserID,
              AuthorUserID: note?.AuthorUserID,
              OwnerUserID: note?.OwnerUserID,
              AssignedUserID: note?.AssignedUserID,
              resolvedOwnerId: ownerIdStr,
              currentUserId: currentUserId,
              matches: matches,
              notePreview: note.NoteText?.substring(0, 50)
            });
          }
          
          return matches;
        });
        console.log(`✅ Filtered to ${filtered.length} notes for user ${currentUserId}`);
      }
    } else if (viewMode === 'all' && selectedUserId) {
      console.log('🔍 ALL NOTES MODE - Filtering for selected user:', selectedUserId);
      const targetUserId = String(selectedUserId);
      filtered = filtered.filter(note => {
        const ownerId =
          note?.UserID ??
          note?.CreatedBy ??
          note?.CreatedByUserID ??
          note?.AuthorUserID ??
          note?.OwnerUserID ??
          note?.AssignedUserID;
        const ownerIdStr = ownerId ? String(ownerId) : null;
        return ownerIdStr === targetUserId;
      });
      console.log(`✅ Filtered to ${filtered.length} notes for user ${targetUserId}`);
    } else {
      console.log('🔍 ALL NOTES MODE - No user filter, showing all notes');
      // Get unique user IDs from all notes
      const uniqueUserIds = new Set();
      notes.forEach(note => {
        const ownerId =
          note?.UserID ??
          note?.CreatedBy ??
          note?.CreatedByUserID ??
          note?.AuthorUserID ??
          note?.OwnerUserID ??
          note?.AssignedUserID;
        if (ownerId) uniqueUserIds.add(String(ownerId));
      });
      console.log('📊 Unique user IDs in all notes:', Array.from(uniqueUserIds));
    }

    filtered = filtered.filter(note => noteMatchesTimeFilter(note));

    // Tag filter
    if (selectedTagFilter && selectedTagFilter.trim()) {
      filtered = filtered.filter(note => {
        if (!note.Tags) return false;
        const noteTags = typeof note.Tags === 'string' 
          ? note.Tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean)
          : Array.isArray(note.Tags) 
            ? note.Tags.map(t => String(t).trim().toLowerCase()).filter(Boolean)
            : [];
        return noteTags.includes(selectedTagFilter.toLowerCase());
      });
    }

    // Type filter (now using activeTypeTab)
    if (activeTypeTab !== 'all') {
      filtered = filtered.filter(note => {
        if (!note.Type) return false;
        return note.Type.trim() === activeTypeTab;
      });
    }

    if (searchTerm.trim()) {
      // Split search term by spaces to allow OR matching (email OR name)
      // This allows searching for "john@example.com John Doe" to match notes containing either email OR name
      const searchTerms = searchTerm.toLowerCase().trim().split(/\s+/).filter(term => term.length > 0);
      filtered = filtered.filter(note => {
        const noteTextLower = note.NoteText?.toLowerCase() || '';
        const tagsLower = note.Tags?.toLowerCase() || '';
        const typeLower = note.Type?.toLowerCase() || '';
        const authorName = `${note.FirstName || ''} ${note.LastName || ''}`.toLowerCase();
        
        // Check if ANY of the search terms match (OR logic)
        // This means a note will match if it contains the email OR the name
        return searchTerms.some(searchTerm => {
          return (
            noteTextLower.includes(searchTerm) ||
            tagsLower.includes(searchTerm) ||
            typeLower.includes(searchTerm) ||
            authorName.includes(searchTerm)
          );
        });
      });
    }

    setFilteredNotes(filtered);
  }, [notes, searchTerm, noteMatchesTimeFilter, viewMode, user?.UserID, selectedUserId, selectedTagFilter, activeTypeTab, currentUserId]);

  // Handle search, email, and name query parameters to filter notes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');
    const emailParam = urlParams.get('email');
    const nameParam = urlParams.get('name');
    
    // Build search term from URL parameters
    if (searchParam) {
      // If 'search' parameter exists, use it directly
      setSearchTerm(searchParam);
    } else if (emailParam || nameParam) {
      // If 'email' or 'name' parameters exist, combine them
      const searchParts = [];
      if (emailParam) searchParts.push(emailParam);
      if (nameParam) searchParts.push(nameParam);
      setSearchTerm(searchParts.join(' '));
    }
    // Note: We don't clear searchTerm if params are removed, to allow user to modify search manually
  }, [location.search]);

  // Handle noteId query parameter to open note modal
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const noteId = urlParams.get('noteId');
    
    if (noteId) {
      const noteIdNum = parseInt(noteId);
      
      // Only open modal if this is a different noteId than we last processed
      if (lastOpenedNoteIdFromQuery !== noteIdNum) {
        const openNoteModal = async () => {
          // First try to find the note in the loaded notes
          let note = notes.find(n => n.NoteID === noteIdNum);
          
          // If note not found in loaded list, fetch it directly from API
          if (!note) {
            try {
              const tenantCode = getProtectedTenantCode();
              const response = await makeFetch(`/api/sales-notes/${noteIdNum}`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
                }
              });
              
              if (response.ok) {
                note = await response.json();
                // Add to notes array if not already there
                setNotes(prevNotes => {
                  const exists = prevNotes.some(n => n.NoteID === noteIdNum);
                  return exists ? prevNotes : [...prevNotes, note];
                });
              } else {
                console.warn(`Note ${noteIdNum} not found or access denied`);
                return;
              }
            } catch (error) {
              console.error('Error fetching note:', error);
              return;
            }
          }
          
          if (note) {
            // Open the full-screen note view modal
            setSelectedNoteForView(note);
            setShowNoteViewModal(true);
            setShowAttachmentsInView(true);
            setLastOpenedNoteIdFromQuery(noteIdNum);
            setFocusedNoteId(noteIdNum);
            
            // Load attachments for this note (for the view modal)
            fetchAttachments(note.NoteID);
            
            // Load relations for this note (for the view modal)
            fetchNoteRelations(note.NoteID);
          }
        };
        
        // Open the modal immediately - if note not in list, fetch it
        openNoteModal();
      }
    } else if (!noteId && lastOpenedNoteIdFromQuery) {
      // Reset when noteId is removed from URL
      setLastOpenedNoteIdFromQuery(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes, location.search, lastOpenedNoteIdFromQuery, loading]);

  // Open/scroll when accessing /notes/:id directly
  useEffect(() => {
    const routeId = params.id;
    if (!routeId || hasScrolledToNote || notes.length === 0) return;
    setFocusedNoteId(parseInt(routeId));
    setTimeout(() => {
      const noteRef = noteRefs.current[routeId];
      if (noteRef) {
        noteRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 500);
  }, [params.id, notes, hasScrolledToNote, currentUserId]);

  // Handle Escape key to close edit modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showEditModal) {
        handleCancelEdit();
      }
    };

    if (showEditModal) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [showEditModal]);

  // Handle Escape key to close view attachment modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showViewAttachmentModal) {
        handleCloseViewAttachment();
      }
    };

    if (showViewAttachmentModal) {
      document.addEventListener('keydown', handleEscapeKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [showViewAttachmentModal]);

  // Check OCR service availability on mount
  useEffect(() => {
    const checkOcrAvailability = async () => {
      try {
        const tenantCode = getProtectedTenantCode();
        const headers = {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        };
        
        // Check if OCR endpoint is available by making a test request
        // Use the same endpoint as OCRManagement component
        const response = await fetch('/api/admin/ocr/status', { headers });
        
        if (response.ok) {
          const data = await response.json();
          // Check if the serviceTest was successful
          const isAvailable = data.serviceTest?.success === true;
          console.log('OCR availability check:', { isAvailable, status: data });
          setOcrAvailable(isAvailable);
        } else {
          // If endpoint doesn't exist or returns error, assume OCR is not available
          console.warn('OCR status endpoint returned error:', response.status);
          setOcrAvailable(false);
        }
      } catch (error) {
        console.warn('OCR availability check failed:', error);
        // On error, assume OCR is not available
        setOcrAvailable(false);
      }
    };

    checkOcrAvailability();
  }, [getProtectedTenantCode]);


  const handleEditNote = async (note) => {
    if (!hasEditRole) return;
    // Don't allow editing if already editing another note
    if (editingNote && editingNote !== note.NoteID) {
      return;
    }
    setEditingNote(note.NoteID);
    setEditNoteText(note.NoteText);
    setEditNoteType(note.Type || '');
    setEditTags(note.Tags || '');
    setError(null);
    setShowEditModal(true);
    setEditingNoteAttachmentFiles([]);
    
    // Load attachments for this note
    try {
      const tenantCode = getProtectedTenantCode();
      const response = await makeFetch(`/api/attachments/SalesNote/${note.NoteID}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        }
      });

      if (response.ok) {
        const data = await response.json();
        setEditingNoteAttachments(data.attachments || []);
      }
    } catch (error) {
      console.warn('Failed to load attachments for editing note', error);
      setEditingNoteAttachments([]);
    }
    
    // Load relations for this note
    try {
      const res = await entityRelationService.getRelatedEntities('SaleNote', note.NoteID, { direction: 'both' });
      const relations = res?.relations || [];
      const entities = relations.map(rel => {
        const entityType = rel.SourceEntityType === 'SaleNote' ? rel.TargetEntityType : rel.SourceEntityType;
        const entityId = rel.SourceEntityType === 'SaleNote' ? rel.TargetEntityID : rel.SourceEntityID;
        const base = {
          type: entityType,
          id: entityId,
          name: rel.TargetName || rel.SourceName || ''
        };
        const relationMeta = {
          CustomerType: rel.CustomerType || rel.TargetCustomerType || rel.SourceCustomerType,
          CompanyName: rel.CompanyName || rel.TargetCompanyName || rel.SourceCompanyName,
          CompanyName_Eng: rel.CompanyName_Eng || rel.TargetCompanyName_Eng || rel.SourceCompanyName_Eng,
          PersonCompanyName: rel.PersonCompanyName || rel.TargetPersonCompanyName || rel.SourcePersonCompanyName,
          PersonCompanyName_Eng: rel.PersonCompanyName_Eng || rel.TargetPersonCompanyName_Eng || rel.SourcePersonCompanyName_Eng,
          CompanyID: rel.CompanyID || rel.TargetCompanyID || rel.SourceCompanyID,
          CompanyCustomerID: rel.CompanyCustomerID || rel.TargetCompanyCustomerID || rel.SourceCompanyCustomerID,
          PersonCompanyID: rel.PersonCompanyID || rel.TargetPersonCompanyID || rel.SourcePersonCompanyID
        };
        return attachCustomerMeta(base, entityType, relationMeta);
      });
      
      // Enrich names where missing (same logic as fetchNoteRelations)
      const enriched = await Promise.all(entities.map(async (e) => {
        if (e.name && e.name.trim() !== '') return e;
        try {
          let detail;
          if (e.type === 'Customer') {
            const resp = await fetch(`/api/customers/${e.id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            if (resp.ok) {
              detail = await resp.json();
              const cust = detail?.customer || detail || {};
              const name = cust.CustomerType === 'Person'
                ? `${cust.FirstName || ''} ${cust.LastName || ''}`.trim() || cust.CustomerName
                : cust.CompanyName || cust.CustomerName;
              const updated = {
                ...e,
                name: name || e.name || `Customer ${e.id}`
              };
              return attachCustomerMeta(updated, 'Customer', cust);
            }
          } else if (e.type === 'Lead') {
            const resp = await fetch(`/api/leads/${e.id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            if (resp.ok) {
              detail = await resp.json();
              const ld = detail?.lead || detail || {};
              const name = ld.Title || ld.ContactName || ld.CompanyName;
              return { ...e, name: name || e.name || `Lead ${e.id}` };
            }
          } else if (e.type === 'Opportunity') {
            const resp = await fetch(`/api/opportunities/${e.id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            if (resp.ok) {
              detail = await resp.json();
              const opp = detail?.opportunity || detail || {};
              const name = opp.OpportunityName || opp.CustomerName;
              return { ...e, name: name || e.name || `Opportunity ${e.id}` };
            }
          } else if (e.type === 'Activity') {
            const resp = await fetch(`/api/activities/${e.id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            if (resp.ok) {
              detail = await resp.json();
              const act = detail?.activity || detail || {};
              const name = act.Subject || `${act.ActivityType || 'Activity'} ${e.id}`;
              return { ...e, name: name || e.name || `Activity ${e.id}` };
            }
          }
        } catch (_e) {
          // ignore enrichment failure
        }
        return { ...e, name: e.name || `${e.type} ${e.id}` };
      }));
      
      const sortedEnriched = mergeSortedCustomerOrder(enriched);
      setEditingNoteRelatedEntities(sortedEnriched);
      setEditingNoteSelectedEntityTypes([...new Set(sortedEnriched.map(e => e.type))]);
    } catch (e) {
      console.warn('Failed to load relations for editing note', e);
      setEditingNoteRelatedEntities([]);
      setEditingNoteSelectedEntityTypes([]);
    }
  };

  const handleCancelEdit = () => {
    setEditingNote(null);
    setEditNoteText('');
    setEditNoteType('');
    setEditTags('');
    setEditingNoteRelatedEntities([]);
    setEditingNoteSelectedEntityTypes([]);
    setEditingNoteAttachments([]);
    setEditingNoteAttachmentFiles([]);
    setShowEditModal(false);
  };

  const handleSaveEdit = async (noteId) => {
    try {
      const tenantCode = getProtectedTenantCode();
      const storedSelectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/sales-notes/${noteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
          ...(storedSelectedRole ? { 'X-Selected-Role': storedSelectedRole } : {})
        },
        body: JSON.stringify({ 
          noteText: editNoteText,
          type: editNoteType || '',
          tags: editTags 
        })
      });

      if (response.ok) {
        const updatedNote = await response.json();
        setNotes(prevNotes => prevNotes.map(note => 
          note.NoteID === noteId ? updatedNote : note
        ));
        
        // Sync relations for the edited note
        if (editingNoteRelatedEntities.length > 0 || noteRelations[noteId]) {
          try {
            // Get current relations
            const currentRes = await entityRelationService.getRelatedEntities('SaleNote', noteId, { direction: 'both' });
            const currentRelations = currentRes?.relations || [];
            const currentKeys = new Set(currentRelations.map(rel => {
              const t = rel.SourceEntityType === 'SaleNote' ? rel.TargetEntityType : rel.SourceEntityType;
              const i = rel.SourceEntityType === 'SaleNote' ? rel.TargetEntityID : rel.SourceEntityID;
              return `${t}:${i}`;
            }));
            
            // Get target relations from editing state
            const targetKeys = new Set(editingNoteRelatedEntities.map(e => `${e.type}:${e.id}`));
            
            // Find relations to create
            const toCreate = editingNoteRelatedEntities.filter(e => !currentKeys.has(`${e.type}:${e.id}`));
            
            // Find relations to delete
            const toDelete = currentRelations.filter(rel => {
              const t = rel.SourceEntityType === 'SaleNote' ? rel.TargetEntityType : rel.SourceEntityType;
              const i = rel.SourceEntityType === 'SaleNote' ? rel.TargetEntityID : rel.SourceEntityID;
              return !targetKeys.has(`${t}:${i}`);
            });
            
            await Promise.allSettled([
              ...toCreate.map(ent => entityRelationService.create({
                sourceEntityType: 'SaleNote',
                sourceEntityID: noteId,
                targetEntityType: ent.type,
                targetEntityID: ent.id,
                relationType: 'related_to'
              })),
              ...toDelete.map(rel => entityRelationService.delete(rel.RelationID))
            ]);
            
            // Refresh relations display
            fetchNoteRelations(noteId);
          } catch (relErr) {
            console.warn('Failed to sync relations for edited note', relErr);
          }
        }
        
        setEditingNote(null);
        setEditNoteText('');
        setEditTags('');
        setEditingNoteRelatedEntities([]);
        setEditingNoteSelectedEntityTypes([]);
        setEditingNoteAttachments([]);
        setEditingNoteAttachmentFiles([]);
        setShowEditModal(false);
        setError(null);
        
        // Upload new attachments if any
        if (editingNoteAttachmentFiles.length > 0) {
          try {
            await handleAttachmentUpload(noteId, editingNoteAttachmentFiles);
            setEditingNoteAttachmentFiles([]);
          } catch (uploadError) {
            console.warn('Failed to upload attachments during edit', uploadError);
          }
        }
        
        // Refresh attachments for the edited note
        fetchAttachments(noteId);
      } else if (response.status === 404) {
        // Note was deleted or doesn't exist
        setNotes(prevNotes => prevNotes.filter(note => note.NoteID !== noteId));
        setEditingNote(null);
        setEditNoteText('');
        setEditTags('');
        setEditingNoteRelatedEntities([]);
        setEditingNoteSelectedEntityTypes([]);
        setEditingNoteAttachments([]);
        setEditingNoteAttachmentFiles([]);
        setShowEditModal(false);
        setError(safeT('Notes.noteDeletedOrNotExists', 'Note was deleted or no longer exists'));
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update note');
      }
    } catch (error) {
      console.error('Error updating note:', error);
      setError(error.message);
    }
  };

  // Check if current user can edit/delete a specific note based on data governance
  // Policy 1: Owner can edit own, Manager/Admin can edit all (with notification)
  // Policy 2: Owner can edit own ONLY, cannot edit others even as Manager (when selected role is not Manager/Admin)
  // Policy 3: Everyone can edit all
  // Note: Since frontend doesn't know the policy type, we show edit buttons for all users with edit role
  // The backend will enforce the actual policy and return 403 if edit is not allowed
  const canEditNote = (note) => {
    if (!hasEditRole || !note) return false;
    
    // Check if user is the owner
    const currentUserId = user?.tenantUserId || user?.userId || user?.UserID;
    const isOwner = String(note.UserID) === String(currentUserId);
    
    // Check if user has Manager/Admin role (based on selected role)
    const isManagerOrAdmin = selectedRole 
      ? ['Manager', 'Admin'].includes(selectedRole)
      : user?.roles?.some(role => ['Manager', 'Admin'].includes(role.RoleName || role));
    
    // Owner can always edit their own notes
    if (isOwner) return true;
    
    // Manager/Admin can edit all notes (Policy 1 and 3)
    // For Policy 2, if selected role is not Manager/Admin, they cannot edit others' notes
    if (isManagerOrAdmin) return true;
    
    // For users with edit role who are not owners and not Manager/Admin:
    // - Policy 1: Backend will block (only owners and managers can edit)
    // - Policy 2: Backend will block (only owners can edit)
    // - Policy 3: Backend will allow (everyone can edit)
    // Since we don't know the policy on frontend, show edit button and let backend enforce
    // This provides better UX - users see edit buttons, backend blocks if policy doesn't allow
    return true; // Show edit button for all users with edit role; backend enforces actual policy
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm(safeT('Notes.confirmDelete', 'Are you sure you want to delete this note?'))) {
      return;
    }

    setDeletingNote(noteId);

    try {
      const tenantCode = getProtectedTenantCode();
      const storedSelectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/sales-notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
          ...(storedSelectedRole ? { 'X-Selected-Role': storedSelectedRole } : {})
        }
      });

      if (response.ok) {
        // Remove the note from the local state
        setNotes(prevNotes => prevNotes.filter(note => note.NoteID !== noteId));
        
        // Show success message with attachment deletion info
        toast.success(t('Notes.noteDeletedWithAttachments'));
        
        // If we're editing this note, cancel the edit
        if (editingNote === noteId) {
          setEditingNote(null);
          setEditNoteText('');
          setEditTags('');
        }
        
        // Clear any errors
        setError(null);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete note');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      setError(error.message);
    } finally {
      setDeletingNote(null);
    }
  };

  // Handle note selection
  const handleNoteSelect = (noteId) => {
    const newSelected = new Set(selectedNotes);
    if (newSelected.has(noteId)) {
      newSelected.delete(noteId);
    } else {
      newSelected.add(noteId);
    }
    setSelectedNotes(newSelected);
    
    // Update select all state
    if (newSelected.size === filteredNotes.length && filteredNotes.length > 0) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedNotes(new Set());
      setSelectAll(false);
    } else {
      const allNoteIds = filteredNotes.map(note => note.NoteID);
      setSelectedNotes(new Set(allNoteIds));
      setSelectAll(true);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedNotes.size === 0) {
      toast.error('Please select at least one note to delete');
      return;
    }

    const count = selectedNotes.size;
    if (!window.confirm(
      safeT('Notes.confirmBulkDelete', `Are you sure you want to delete ${count} note${count !== 1 ? 's' : ''}? This action cannot be undone.`)
    )) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const noteIds = Array.from(selectedNotes);
      const tenantCode = getProtectedTenantCode();
      
      // Delete notes one by one (you could implement a bulk delete endpoint if needed)
      const storedSelectedRole = localStorage.getItem('selectedRole');
      const deletePromises = noteIds.map(noteId =>
        fetch(`/api/sales-notes/${noteId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
            ...(storedSelectedRole ? { 'X-Selected-Role': storedSelectedRole } : {})
          }
        })
      );

      const results = await Promise.allSettled(deletePromises);
      const successCount = results.filter(r => r.status === 'fulfilled' && r.value.ok).length;
      const failCount = results.filter(r => r.status === 'rejected' || !r.value.ok).length;
      
      toast.success(
        `Successfully deleted ${successCount} note${successCount !== 1 ? 's' : ''}` +
        (failCount > 0 ? `. ${failCount} failed.` : '')
      );
      
      // Clear selection
      setSelectedNotes(new Set());
      setSelectAll(false);
      
      // Refresh the list
      await fetchNotes();
      
    } catch (err) {
      toast.error(err.message || 'Failed to delete notes');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening note view modal on double-click
  const handleNoteDoubleClick = (note) => {
    setSelectedNoteForView(note);
    setShowNoteViewModal(true);
    setShowAttachmentsInView(true); // Default to showing attachments
  };

  // AI Analysis - Under Construction
  const handleAiAnalysis = async (note, analysisType = 'complete') => {
    toast.error(safeT('Notes.underConstruction', 'This feature is under construction'));
  };

  // Removed analyzed notes visibility helpers

  // Removed analysis results fetch function (UI removed)

  // Removed analysis results deletion function (UI removed)

  // Handle creating customer from email with AI-extracted data
  const handleCreateCustomerFromEmail = (email, extractedData, noteText) => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/13508a14-e751-4717-a7dd-603439d1d1ed',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Notes.js:2031',message:'handleCreateCustomerFromEmail ENTRY',data:{email,extractedData,hasNoteText:!!noteText},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
    // #endregion
    
    console.log('handleCreateCustomerFromEmail called with:', { email, extractedData, noteText: noteText?.substring(0, 100) });
    
    // Prepare person data for customer form - ensure email is always set
    const personData = {
      email: email || extractedData?.email || '',
      firstName: extractedData?.firstName || '',
      lastName: extractedData?.lastName || '',
      firstName_eng: extractedData?.firstName_eng || '',
      lastName_eng: extractedData?.lastName_eng || '',
      phone: extractedData?.phone || '',
      title: extractedData?.title || '',
      jobTitle: extractedData?.jobTitle || '',
      department: extractedData?.department || ''
    };

    console.log('Navigating to customer form with personData:', personData);

    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/13508a14-e751-4717-a7dd-603439d1d1ed',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Notes.js:2051',message:'BEFORE navigate call',data:{personData,customerType:'Person'},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
    // #endregion

    // Navigate to customer form with pre-filled data
    navigate('/customers/new', {
      state: {
        customerType: 'Person',
        prefillData: personData,
        sourceNote: noteText ? noteText.substring(0, 200) : '' // Store note reference
      }
    });
    
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/13508a14-e751-4717-a7dd-603439d1d1ed',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Notes.js:2062',message:'AFTER navigate call',data:{navigateCalled:true},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1'})}).catch(()=>{});
    // #endregion
  };

  // Helper function to detect and convert URLs and emails to hyperlinks
  const linkifyText = (text, noteText = '') => {
    if (!text) return text;

    // Regex patterns for URLs and emails
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

    // Split text by newlines to preserve formatting
    const lines = text.split('\n');

    return lines.map((line, lineIndex) => {
      // Process URLs in the line
      let processedLine = line.replace(urlRegex, (match) => {
        return `<URL_LINK>${match}</URL_LINK>`;
      });

      // Process emails in the line
      processedLine = processedLine.replace(emailRegex, (match) => {
        return `<EMAIL_LINK>${match}</EMAIL_LINK>`;
      });

      // Split by our markers and create JSX
      const parts = processedLine.split(/(<URL_LINK>.*?<\/URL_LINK>|<EMAIL_LINK>.*?<\/EMAIL_LINK>)/);

      const jsxParts = parts.map((part, index) => {
        if (part.startsWith('<URL_LINK>') && part.endsWith('</URL_LINK>')) {
          const url = part.slice(10, -11); // Remove markers
          return (
            <a
              key={`${lineIndex}-${index}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {url}
            </a>
          );
        } else if (part.startsWith('<EMAIL_LINK>') && part.endsWith('</EMAIL_LINK>')) {
          const email = part.slice(12, -13); // Remove markers
          return (
            <EmailHoverPopover
              key={`${lineIndex}-${index}`}
              email={email}
              noteText={noteText}
              getProtectedTenantCode={getProtectedTenantCode}
              onCreateCustomer={handleCreateCustomerFromEmail}
            >
              <a
                href={`mailto:${email}`}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
                onClick={(e) => e.stopPropagation()}
              >
                {email}
              </a>
            </EmailHoverPopover>
          );
        } else {
          return part;
        }
      });

      // Return the processed line with a line break if not the last line
      return (
        <span key={lineIndex}>
          {jsxParts}
          {lineIndex < lines.length - 1 && <br />}
        </span>
      );
    });
  };

  const renderNoteContent = (text) => {
    if (!text) return null;

    const lines = text.split('\n');

    return lines.map((line, lineIndex) => {
      const timestampMatch = line.match(/^\[\d{2}\/\d{2}\/\d{4},\s*\d{2}:\d{2}:\d{2}\]\s*/);
      let content = line;
      const lineNodes = [];

      if (timestampMatch) {
        const timestampText = timestampMatch[0];
        content = line.slice(timestampText.length);
        lineNodes.push(
          <span
            key={`ts-${lineIndex}`}
            className="text-xs italic font-mono text-gray-500 dark:text-gray-400 mr-2"
          >
            {timestampText.trim()}
          </span>
        );
      }

      const markerIndex = content.indexOf(STICKER_MARKER);

      const appendNodes = (value) => {
        if (value === null || value === undefined) return;
        if (Array.isArray(value)) {
          value.forEach(appendNodes);
        } else {
          lineNodes.push(value);
        }
      };

      if (markerIndex !== -1) {
        const beforeMarker = content.slice(0, markerIndex);
        const rawPayload = content.slice(markerIndex + STICKER_MARKER.length);
        const parsedPayload = parseStickerPayload(rawPayload);

        if (parsedPayload) {
          const closingIndex = rawPayload.lastIndexOf('}');
          const trailingText = closingIndex !== -1 && closingIndex + 1 < rawPayload.length
            ? rawPayload.slice(closingIndex + 1)
            : '';

          appendNodes(linkifyText(beforeMarker, text));
          lineNodes.push(<StickerPreview key={`sticker-${lineIndex}`} data={parsedPayload} />);
          if (trailingText) {
            appendNodes(linkifyText(trailingText, text));
          }
        } else {
          appendNodes(linkifyText(content, text));
        }
      } else {
        appendNodes(linkifyText(content, text));
      }

      return (
        <React.Fragment key={`line-${lineIndex}`}>
          {lineNodes}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!hasEditRole) return;
    if (!newNote.trim()) return;

    console.log('Adding note:', { noteText: newNote, tags });
    console.log('Current notes before adding:', notes);

    try {
      const tenantCode = getProtectedTenantCode();
      const response = await fetch('/api/sales-notes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
        },
        body: JSON.stringify({ noteText: newNote, type: noteType || '', tags })
      });

      console.log('Add note response status:', response.status);
      console.log('Add note response ok:', response.ok);

      if (response.ok) {
        const addedNote = await response.json();
        console.log('Added note response:', addedNote);
        console.log('Current notes before update:', notes);
        const updatedNotes = [addedNote, ...notes];
        console.log('Updated notes array:', updatedNotes);
        setNotes(updatedNotes);
        setNewNote('');
        setNoteType('Conversation');
        setTags('');
        // Create relations for the new note if selected
        if (newNoteRelatedEntities.length > 0) {
          try {
            await Promise.allSettled(
              newNoteRelatedEntities.map(ent => entityRelationService.create({
                sourceEntityType: 'SaleNote',
                sourceEntityID: addedNote.NoteID,
                targetEntityType: ent.type,
                targetEntityID: ent.id,
                relationType: 'related_to'
              }))
            );
            fetchNoteRelations(addedNote.NoteID);
          } catch (relErr) {
            console.warn('Failed to create relations for new note', relErr);
          }
        }
        setNewNoteRelatedEntities([]);
        setNewNoteSelectedEntityTypes([]);
        if (newNoteAttachmentFiles.length > 0) {
          try {
            await handleAttachmentUpload(addedNote.NoteID, newNoteAttachmentFiles);
            toast.success(safeT('Notes.attachmentsUploaded', `Successfully uploaded ${newNoteAttachmentFiles.length} attachment(s)`));
          } catch (uploadError) {
            console.error('Failed to upload attachments for new note:', uploadError);
            toast.error(safeT('Notes.failedToUploadAttachments', 'Failed to upload attachments. Please try again.'));
          }
        }

        setNewNoteAttachmentFiles([]);
        setShowAddNoteModal(false); // Close modal after successful submission
        console.log('State should be updated');
        
        // Fetch attachments for the newly added note
        fetchAttachments(addedNote.NoteID);
        
        // If we're viewing all notes, refresh to get the latest data
        if (viewMode === 'all') {
          fetchNotes();
        }
      } else {
        const errorData = await response.json();
        console.error('Failed to add note:', errorData);
        throw new Error(errorData.error || 'Failed to add note');
      }
    } catch (error) {
      console.error('Error in handleAddNote:', error);
      setError(error.message);
    }
  };

  // ============================================================
  // SUMMARY GENERATION FUNCTIONS
  // ============================================================

  const handleOpenSummaryModal = () => {
    setShowSummaryModal(true);
    // Reset options to defaults
    setSummaryOptions({
      style: 'focus_points',
      filterBy: 'none',
      filterValue: '',
      scope: viewMode || 'my', // Use current view mode as default
      dateFrom: '',
      dateTo: '',
      datePreset: '',
      entityType: '',
      entityId: '',
      entityName: '', // Store entity name for display
      language: 'th', // Default to Thai
      noteTypes: [], // Reset note types
      includeAttachments: false, // Reset attachment inclusion
      additionalInstructions: '' // Reset additional instructions
    });
    setTagFilterMode('popular');
    setTagSearchQuery('');
    
    // Compute tag frequencies from current notes
    computeTagFrequencies();
    // Compute available note types
    computeAvailableNoteTypes();
  };

  const computeTagFrequencies = () => {
    const frequencies = {};
    notes.forEach(note => {
      if (note.Tags) {
        const noteTags = note.Tags.split(',').map(t => t.trim()).filter(Boolean);
        noteTags.forEach(tag => {
          frequencies[tag] = (frequencies[tag] || 0) + 1;
        });
      }
    });
    setTagFrequencyMap(frequencies);
  };

  const computeAvailableNoteTypes = () => {
    const typeSet = new Set();
    notes.forEach(note => {
      if (note.Type) {
        typeSet.add(note.Type.trim());
      }
    });
    const sortedTypes = Array.from(typeSet).sort();
    setAvailableNoteTypes(sortedTypes);
  };

  // Helper function to calculate date ranges
  const getDateRangeFromPreset = (preset) => {
    const now = new Date();
    let dateFrom = '';
    let dateTo = '';

    switch (preset) {
      case 'today':
        dateFrom = dateTo = now.toISOString().split('T')[0];
        break;
      
      case 'thisweek': {
        // This week (Monday to Sunday)
        const dayOfWeek = now.getDay();
        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(now);
        monday.setDate(now.getDate() + diffToMonday);
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        dateFrom = monday.toISOString().split('T')[0];
        dateTo = sunday.toISOString().split('T')[0];
        break;
      }
      
      case 'lastweek': {
        // Last week (Last Monday to Last Sunday)
        const dayOfWeek = now.getDay();
        const diffToLastMonday = dayOfWeek === 0 ? -13 : -6 - dayOfWeek;
        const lastMonday = new Date(now);
        lastMonday.setDate(now.getDate() + diffToLastMonday);
        const lastSunday = new Date(lastMonday);
        lastSunday.setDate(lastMonday.getDate() + 6);
        dateFrom = lastMonday.toISOString().split('T')[0];
        dateTo = lastSunday.toISOString().split('T')[0];
        break;
      }
      
      case 'thismonth': {
        // This month (1st to today or end of month)
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        dateFrom = firstDay.toISOString().split('T')[0];
        dateTo = lastDay.toISOString().split('T')[0];
        break;
      }
      
      case 'lastmonth': {
        // Last month (1st to last day)
        const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
        dateFrom = firstDay.toISOString().split('T')[0];
        dateTo = lastDay.toISOString().split('T')[0];
        break;
      }
      
      default:
        break;
    }

    return { dateFrom, dateTo };
  };

  const handleDatePresetChange = (preset) => {
    if (preset === 'custom') {
      setSummaryOptions(prev => ({ ...prev, datePreset: 'custom', dateFrom: '', dateTo: '' }));
    } else if (preset) {
      const { dateFrom, dateTo } = getDateRangeFromPreset(preset);
      setSummaryOptions(prev => ({ ...prev, datePreset: preset, dateFrom, dateTo }));
    } else {
      setSummaryOptions(prev => ({ ...prev, datePreset: '', dateFrom: '', dateTo: '' }));
    }
  };

  const handleCloseSummaryModal = () => {
    setShowSummaryModal(false);
    setSummaryProgress({
      step: '',
      current: 0,
      total: 0,
      message: ''
    });
  };

  // Wrapper function to call the extracted summary generation service
  const handleGenerateSummary = async () => {
    await generateSummary({
      summaryOptions,
      hasEditRole,
      user,
      getProtectedTenantCode,
      setGeneratingSummary,
      setSummaryProgress,
      setNotes,
      handleCloseSummaryModal,
      fetchNotes,
      t,
      toast
    });
  };



  const handleAttachmentUpload = async (noteId, files) => {
    try {
      await handleAttachmentUploadService(
        noteId,
        files,
        getProtectedTenantCode,
        t,
        setError,
        setUploadingAttachments,
        fetchAttachments
      );
    } catch (error) {
      // Service now throws errors, so we re-throw them
      throw error;
    }
  };

  const handleAttachmentModalOpen = (note) => {
    setSelectedNoteForAttachment(note);
    setAttachmentFiles([]);
    setShowAttachmentModal(true);
  };

  const handleAttachmentModalClose = () => {
    setShowAttachmentModal(false);
    setSelectedNoteForAttachment(null);
    setAttachmentFiles([]);
  };

  const handleAttachmentModalUpload = async () => {
    if (!selectedNoteForAttachment || attachmentFiles.length === 0) return;
    
    try {
      const noteId = selectedNoteForAttachment.NoteID;
      const filesToUpload = [...attachmentFiles];
      
      // Upload attachments
      await handleAttachmentUpload(noteId, filesToUpload);
      
      // Wait a bit to ensure backend has processed
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Refresh attachments - the service should already do this, but ensure it happens
      await fetchAttachments(noteId);
      
      // Show success message
      toast.success(safeT('Notes.attachmentsUploaded', `Successfully uploaded ${filesToUpload.length} attachment(s)`));
      
      // Close modal after successful upload
      handleAttachmentModalClose();
    } catch (error) {
      console.error('Error uploading attachments:', error);
      toast.error(safeT('Notes.failedToUploadAttachments', 'Failed to upload attachments. Please try again.'));
      // Don't close modal on error so user can try again
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setAttachmentFiles(files);
  };

  const handleDownloadAttachment = async (attachment) => {
    await handleDownloadAttachmentService(attachment, getProtectedTenantCode, t, setError);
  };

  const handleDeleteAttachment = async (attachment, noteId) => {
    await handleDeleteAttachmentService(
      attachment,
      noteId,
      getProtectedTenantCode,
      t,
      setError,
      setAttachments,
      setDeletingAttachments
    );
  };

  // Helper to load image with authentication and create blob URL
  const loadImageAsBlob = async (attachmentId) => {
    return await loadImageAsBlobService(
      attachmentId,
      getProtectedTenantCode,
      imageBlobUrls,
      imageLoading,
      imageLoadErrors,
      setImageBlobUrls,
      setImageLoading,
      setImageLoadErrors
    );
  };

  // Load images when gallery view is shown
  useEffect(() => {
    // Find all notes with gallery view enabled
    const notesWithGalleryView = Object.entries(attachmentViewMode)
      .filter(([_, viewMode]) => viewMode === 'gallery')
      .map(([noteId]) => noteId);

    notesWithGalleryView.forEach(noteId => {
      if (attachments[noteId]) {
        attachments[noteId].forEach(attachment => {
          const isImage = attachment.MimeType && attachment.MimeType.toLowerCase().startsWith('image/');
          const attachmentId = attachment.AttachmentID;
          // Only load if we don't already have it, haven't errored, and aren't loading
          if (isImage && 
              !imageBlobUrls[attachmentId] && 
              !imageLoadErrors[attachmentId] && 
              !imageLoading[attachmentId]) {
            loadImageAsBlob(attachmentId);
          }
        });
      }
    });
    // Only run when attachmentViewMode changes or when new attachments are loaded
    // We use JSON.stringify to detect changes in the attachments object keys
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentViewMode, JSON.stringify(Object.keys(attachments))]);

  // Cleanup blob URLs on unmount or when attachments change
  useEffect(() => {
    return () => {
      Object.values(imageBlobUrls).forEach(url => {
        if (url && typeof url === 'string' && url.startsWith('blob:')) {
          try {
            window.URL.revokeObjectURL(url);
          } catch (e) {
            console.warn('Error revoking blob URL:', e);
          }
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewAttachment = async (attachment) => {
    await handleViewAttachmentService(
      attachment,
      getProtectedTenantCode,
      t,
      setError,
      setViewingAttachment,
      setSelectedAttachmentForView,
      setAttachmentViewUrl,
      setShowViewAttachmentModal
    );
  };

  const handleCloseViewAttachment = () => {
    handleCloseViewAttachmentService(
      setShowViewAttachmentModal,
      setSelectedAttachmentForView,
      attachmentViewUrl,
      setAttachmentViewUrl,
      setModalSize
    );
  };

  // Handle modal resizing
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const container = document.querySelector('.modal-container');
      if (!container) return;
      
      const rect = container.getBoundingClientRect();
      const newWidth = Math.max(600, Math.min(window.innerWidth - 100, e.clientX - rect.left));
      const newHeight = Math.max(400, Math.min(window.innerHeight - 100, e.clientY - rect.top));
      
      setModalSize({
        width: `${newWidth}px`,
        height: `${newHeight}px`
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Show loading state while translations are loading
  if (!translationsReady || !ready) {
    return (
      <div className="p-3 sm:p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading translations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
      <div className="w-[90%] mx-auto">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <img 
              src="icon/notes.png" 
              alt="Notes Icon" 
              className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 object-contain"
            />
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">{safeT('Notes.pagetitle', 'Notes')}</h1>
          </div>
          
          {/* View Mode Toggle - Mobile Optimized */}
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Display Mode Toggle (Card/List) */}
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1 transition-colors duration-300">
              <button
                onClick={() => setDisplayMode('card')}
                className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md flex items-center justify-center space-x-1 sm:space-x-2 transition-colors touch-target ${
                  displayMode === 'card'
                    ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
                title="Card View"
              >
                <LayoutGrid className="h-4 w-4" />
                <span className="text-xs sm:text-sm font-medium hidden sm:inline">Card</span>
              </button>
              <button
                onClick={() => setDisplayMode('list')}
                className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md flex items-center justify-center space-x-1 sm:space-x-2 transition-colors touch-target ${
                  displayMode === 'list'
                    ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
                title="List View"
              >
                <List className="h-4 w-4" />
                <span className="text-xs sm:text-sm font-medium hidden sm:inline">List</span>
              </button>
            </div>

            {/* Filter Mode Toggle (My Notes/All Notes) */}
            <div className="flex bg-gray-200 dark:bg-gray-700 rounded-lg p-1 transition-colors duration-300">
              <button
                onClick={() => {
                  setViewMode('my');
                  setSelectedUserId('');
                }}
                className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors touch-target ${
                  viewMode === 'my'
                    ? 'bg-white dark:bg-gray-800 text-teal-600 dark:text-teal-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
                }`}
              >
                {safeT('Notes.myNotes', 'My Notes')}
              </button>
              {(() => {
                // Allow all salespeople to see all notes
                const hasSalespersonRole = selectedRole
                  ? ['Salesperson', 'Manager', 'Admin'].includes(selectedRole)
                  : user?.roles?.some(role => 
                      role.RoleName === 'Salesperson' || 
                      role.RoleName === 'Manager' || 
                      role.RoleName === 'Admin'
                    );
                console.log('Has salesperson role:', hasSalespersonRole);
                console.log('Selected role:', selectedRole);
                console.log('User roles:', user?.roles);
                return hasSalespersonRole;
              })() && (
                <button
                  onClick={() => setViewMode('all')}
                  className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors touch-target ${
                    viewMode === 'all'
                      ? 'bg-white dark:bg-gray-800 text-teal-600 dark:text-teal-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100'
                  }`}
                >
                  {safeT('Notes.allNotes', 'All Notes')}
                </button>
              )}
            </div>
            
            {/* User Selection Dropdown */}
            {viewMode === 'all' && (() => {
              // Allow all salespeople to see the user dropdown
              const hasSalespersonRole = selectedRole
                ? ['Salesperson', 'Manager', 'Admin'].includes(selectedRole)
                : user?.roles?.some(role => 
                    role.RoleName === 'Salesperson' || 
                    role.RoleName === 'Manager' || 
                    role.RoleName === 'Admin'
                  );
              console.log('Dropdown - Has salesperson role:', hasSalespersonRole);
              console.log('Dropdown - Selected role:', selectedRole);
              return hasSalespersonRole;
            })() && (
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-300"
              >
                <option value="">{safeT('Notes.allUsers', 'All Users')}</option>
                {allUsers.map(user => (
                  <option key={user.UserID} value={user.UserID}>
                    {user.FirstName} {user.LastName}
                  </option>
                ))}
              </select>
            )}
            
            {/* Filter Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium touch-target no-tap-highlight transition-all ${
                searchTerm || selectedTagFilter || timeFilter !== 'all'
                  ? 'bg-orange-500 dark:bg-orange-600 text-white hover:bg-orange-600 dark:hover:bg-orange-700'
                  : showFilters
                  ? 'bg-gray-600 dark:bg-gray-500 text-white hover:bg-gray-700 dark:hover:bg-gray-600'
                  : 'bg-gray-500 dark:bg-gray-600 text-white hover:bg-gray-600 dark:hover:bg-gray-700'
              }`}
              title="Toggle filters"
            >
              <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="hidden sm:inline">{safeT('common.filter', 'Filter')}</span>
              {(searchTerm || selectedTagFilter || timeFilter !== 'all') && (
                <span className="bg-white/20 px-1 sm:px-1.5 py-0.5 rounded-full text-xs font-bold">
                  {(searchTerm ? 1 : 0) + (selectedTagFilter ? 1 : 0) + (timeFilter !== 'all' ? 1 : 0)}
                </span>
              )}
            </button>
          </div>
        </div>
        
        {/* Search and Filter Section - Mobile Optimized - Hidden by Default */}
        {showFilters && (
          <div className={`p-3 sm:p-4 rounded-lg shadow border transition-colors duration-200 mb-4 sm:mb-6 ${
            searchTerm || selectedTagFilter || timeFilter !== 'all'
              ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
              : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          }`}>
            {(searchTerm || selectedTagFilter || timeFilter !== 'all') && (
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                  🔍 Filters Active
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedTagFilter('');
                    setTimeFilter('all');
                    setSelectedMonth('');
                  }}
                  className="text-xs text-orange-600 dark:text-orange-400 hover:underline"
                >
                  Clear All
                </button>
              </div>
            )}
            <div>
          {/* Search and Tag Filter Row */}
          <div className="flex flex-col sm:flex-row gap-3 mb-3">
            {/* Search Box - 50% width */}
            <div className="relative flex-1 sm:w-1/2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={safeT('Notes.searchPlaceholder', 'Search notes by content, tags, or author...')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-300"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <svg className="h-5 w-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            
            {/* Tag Filter - Right side of search */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-1 sm:w-1/2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                {safeT('Notes.filterByTag', safeT('common.tags', 'Filter by Tag'))}:
              </label>
              <div className="flex gap-2 flex-1">
                <select
                  value={selectedTagFilter}
                  onChange={(e) => setSelectedTagFilter(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-300"
                >
                  <option value="">{safeT('Notes.allTags', safeT('common.all', 'All Tags'))}</option>
                  {availableTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
                {selectedTagFilter && (
                  <button
                    onClick={() => setSelectedTagFilter('')}
                    className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors whitespace-nowrap"
                    title={safeT('common.clear', 'Clear tag filter')}
                  >
                    {safeT('common.clear', 'Clear')}
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Time Filter */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {safeT('Notes.timeFilter', 'Time range')}:
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setTimeFilter('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  timeFilter === 'all'
                    ? 'bg-teal-600 dark:bg-teal-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {safeT('Notes.timeFilterAll', 'All time')}
              </button>
              <button
                type="button"
                onClick={() => setTimeFilter('today')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  timeFilter === 'today'
                    ? 'bg-teal-600 dark:bg-teal-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {safeT('Notes.timeFilterToday', 'Today')}
              </button>
              <button
                type="button"
                onClick={() => setTimeFilter('thisWeek')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  timeFilter === 'thisWeek'
                    ? 'bg-teal-600 dark:bg-teal-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {safeT('Notes.timeFilterThisWeek', 'This week')}
              </button>
              <button
                type="button"
                onClick={() => setTimeFilter('thisMonth')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  timeFilter === 'thisMonth'
                    ? 'bg-teal-600 dark:bg-teal-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {safeT('Notes.timeFilterThisMonth', 'This month')}
              </button>
              <button
                type="button"
                onClick={() => setTimeFilter('month')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                  timeFilter === 'month'
                    ? 'bg-teal-600 dark:bg-teal-500 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {safeT('Notes.timeFilterSelectMonth', 'Select month')}
              </button>
            </div>
          </div>
          {timeFilter === 'month' && (
            <div className="flex items-center gap-2 mb-3">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {safeT('Notes.chooseMonth', 'Month')}:
              </label>
              <input
                type="month"
                value={selectedMonth}
                max={`${new Date().getFullYear()}-12`}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-300"
              />
            </div>
          )}
          
          {/* Note Count - Below Filters */}
          <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium">{filteredNotes.length}</span> {filteredNotes.length === 1 ? safeT('Notes.note', 'note') : safeT('Notes.notes', 'notes')}
              {(searchTerm || selectedTagFilter) && notes.length !== filteredNotes.length && (
                <span className="ml-2 text-gray-500 dark:text-gray-400">
                  ({safeT('Notes.filteredFrom', 'filtered from')} {notes.length} {safeT('Notes.total', 'total')})
                </span>
              )}
            </p>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {safeT('Notes.perPage', 'Notes per page')}:
                </label>
                <select
                  value={notesPerPage}
                  onChange={(e) => setNotesPerPage(parseInt(e.target.value, 10))}
                  className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors duration-300"
                >
                  {[10, 20, 50, 100].map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setNotesPage(prev => Math.max(1, prev - 1))}
                  disabled={notesPage <= 1 || loading}
                  className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {safeT('common.previous', 'Previous')}
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {safeT('common.page', 'Page')} {notesPage}
                </span>
                <button
                  type="button"
                  onClick={() => setNotesPage(prev => prev + 1)}
                  disabled={!hasMoreNotes || loading}
                  className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {safeT('common.next', 'Next')}
                </button>
              </div>
            </div>
          </div>
            </div>
          </div>
        )}
        
        {/* Tab Navigation by Note Type - Mobile Optimized */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 mb-4 sm:mb-6">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between gap-2 sm:gap-4">
              <nav className="flex space-x-2 sm:space-x-4 md:space-x-8 px-2 sm:px-4 md:px-6 overflow-x-auto scroll-smooth-mobile flex-1" aria-label="Note Type Tabs">
                {/* All Types Tab */}
                <button
                  onClick={() => setActiveTypeTab('all')}
                  className={`py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap touch-target ${
                    activeTypeTab === 'all'
                      ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Grid className="h-4 w-4 flex-shrink-0" />
                  <span>{safeT('Notes.allTypes', 'All Types')}</span>
                  <span className="ml-1 sm:ml-2 bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 py-0.5 px-1.5 sm:px-2.5 rounded-full text-xs font-medium">
                    {typeCounts.all ?? 0}
                  </span>
                </button>
                
                {/* Dynamic Type Tabs */}
                {availableTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => setActiveTypeTab(type)}
                    className={`py-3 sm:py-4 px-1 sm:px-2 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap touch-target ${
                      activeTypeTab === type
                        ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <File className="h-4 w-4 flex-shrink-0" />
                    <span>{type}</span>
                    <span className="ml-1 sm:ml-2 bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 py-0.5 px-1.5 sm:px-2.5 rounded-full text-xs font-medium">
                      {typeCounts[type] ?? 0}
                    </span>
                  </button>
                ))}
              </nav>
              
              {/* Bulk Action Buttons - On the right side of tabs */}
              {selectedNotes.size > 0 && (
                <div className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4 flex-shrink-0">
                  <div className="bg-teal-100 dark:bg-teal-800 border border-teal-200 dark:border-teal-600 rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 mr-1 sm:mr-2">
                    <span className="text-xs sm:text-sm text-teal-700 dark:text-teal-300 font-medium whitespace-nowrap">
                      {selectedNotes.size} selected
                    </span>
                  </div>
                  
                  <button
                    onClick={handleBulkDelete}
                    disabled={loading}
                    className="bg-red-600 dark:bg-red-500 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-red-700 dark:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm font-medium touch-target no-tap-highlight"
                    title="Delete selected notes"
                  >
                    <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 flex-shrink-0" />
                    <span className="hidden sm:inline">Delete</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setSelectedNotes(new Set());
                      setSelectAll(false);
                    }}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 touch-target"
                    title="Clear selection"
                  >
                    <X className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        {hasEditRole && (
          <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3">
            {/* Create Summary Button - Left Side */}
            <button
              onClick={handleOpenSummaryModal}
              className="inline-flex items-center justify-center px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 dark:from-cyan-600 dark:to-teal-600 dark:hover:from-cyan-700 dark:hover:to-teal-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 text-sm transition-all duration-300 transform hover:scale-105"
            >
              <Brain className="w-5 h-5 mr-2" />
              <span className="flex flex-col items-start sm:block">
                <span className="font-bold">{safeT('Notes.createSummary', 'Create Summary')}</span>
                <span className="text-xs font-normal opacity-90 sm:hidden">{safeT('Notes.aiPowered', 'AI-Powered')}</span>
              </span>
            </button>

            {/* Add Note Button - Right Side, Larger */}
            <button
              onClick={() => setShowAddNoteModal(true)}
              className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 dark:from-cyan-500 dark:to-teal-500 dark:hover:from-cyan-600 dark:hover:to-teal-600 text-white font-bold rounded-lg shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 text-base sm:text-lg transition-all duration-300 transform hover:scale-105"
            >
              <PlusCircle className="w-6 h-6 sm:w-7 sm:h-7 mr-2" />
              <span className="flex flex-col items-start sm:block">
                <span>{safeT('Notes.addNote', 'Add Note')}</span>
                <span className="text-xs font-normal opacity-90 sm:hidden">{safeT('Notes.createNew', 'Create New')}</span>
              </span>
            </button>
          </div>
        )}

        {/* Notes Rendering - Card or List View */}
        {displayMode === 'card' ? (
          /* Card View */
          <div>
            {loading && <p className="text-gray-600 dark:text-gray-400 text-sm">{safeT('Notes.loading', 'Loading...')}</p>}
            {error && <p className="text-red-500 dark:text-red-400 text-sm">{error}</p>}
            {!loading && filteredNotes.length === 0 ? (
              <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-8">
                {searchTerm ? (
                  <div>
                    <p>{safeT('Notes.noSearchResults', 'No notes found matching your search.')}</p>
                    <button
                      onClick={() => setSearchTerm('')}
                      className="mt-2 text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 underline text-sm"
                    >
                      {safeT('Notes.clearSearch', 'Clear search')}
                    </button>
                  </div>
                ) : (
                  <p>{safeT('Notes.noNotes', 'No notes available')}</p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredNotes.map(note => {
                  const isSelected = selectedNotes.has(note.NoteID);
                  
                  return (
                    <div
                      key={note.NoteID}
                      ref={el => noteRefs.current[note.NoteID] = el}
                      onDoubleClick={() => handleNoteDoubleClick(note)}
                      className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border-2 transition-all duration-200 hover:shadow-md cursor-pointer ${
                        isSelected
                          ? 'border-teal-500 dark:border-teal-400 bg-teal-50 dark:bg-teal-900/20'
                          : focusedNoteId === note.NoteID 
                          ? 'border-yellow-400 ring-2 ring-yellow-400 ring-opacity-50' 
                          : 'border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-600'
                      }`}
                    >
                      {/* Card Header */}
                      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-start space-x-3 flex-1 min-w-0">
                            {/* Avatar */}
                            <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                              <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm">
                                {note.FirstName ? note.FirstName.charAt(0) : 'U'}
                              </span>
                            </div>
                            
                            {/* Name and Date */}
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                {`${note.FirstName} ${note.LastName}`}
                              </h3>
                              <div className="flex items-center gap-2 mt-1 flex-wrap">
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {new Date(note.NoteDate).toLocaleDateString('th-TH', {
                                    timeZone: 'Asia/Bangkok',
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                                {note.Type && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300">
                                    {note.Type}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Minimal Action Buttons - Only Delete + Selection */}
                          <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                            {canEditNote(note) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteNote(note.NoteID);
                                }}
                                disabled={deletingNote === note.NoteID}
                                className={`p-1.5 transition-colors rounded ${
                                  deletingNote === note.NoteID 
                                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                                    : 'text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                                }`}
                                title={safeT('common.delete', 'Delete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                            
                            {/* Selection Checkbox */}
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleNoteSelect(note.NoteID)}
                              onClick={(e) => e.stopPropagation()}
                              className="h-4 w-4 text-teal-600 dark:text-teal-400 focus:ring-teal-500 dark:focus:ring-teal-400 border-gray-300 dark:border-gray-600 rounded cursor-pointer ml-1"
                              title="Select note"
                            />
                          </div>
                        </div>
                      </div>
                    
                    {/* Card Body - Note Content */}
                    <div className="p-4">
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md transition-colors duration-300 mb-3">
                        <div className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed break-words line-clamp-4">
                          {isNoteLong(note.NoteText) && !expandedNotes[note.NoteID] ? (
                            <>
                              {renderNoteContent(truncateNote(note.NoteText))}
                              <span className="text-gray-400 dark:text-gray-500">...</span>
                            </>
                          ) : (
                            renderNoteContent(note.NoteText)
                          )}
                        </div>
                        {isNoteLong(note.NoteText) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNoteDoubleClick(note);
                            }}
                            className="mt-2 text-xs text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 font-medium"
                          >
                            {safeT('Notes.showMore', 'Show More')}
                          </button>
                        )}
                      </div>
                      
                      {/* Related Entities - Compact */}
                      {noteRelations[note.NoteID] && noteRelations[note.NoteID].length > 0 && (
                        <div className="mb-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{safeT('Notes.relatedEntities', 'Related')}</div>
                          <div className="flex flex-wrap gap-1">
                            {noteRelations[note.NoteID].slice(0, 3).map((e, idx) => {
                              const colors = getEntityColors(e.type, e.customerType);
                              return (
                                <a
                                  key={`${e.type}-${e.id}-${idx}`}
                                  href={getEntityRoute(e.type.toLowerCase(), e.id)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`flex items-center space-x-1 px-2 py-1 text-xs border rounded transition-colors ${colors.border} ${colors.bg} ${colors.hoverBg} truncate max-w-full`}
                                  title={e.name || `#${e.id}`}
                                >
                                  {getEntityIcon(e.type, 'h-3 w-3 flex-shrink-0', e.customerType)}
                                  <span className="truncate">{e.name || `#${e.id}`}</span>
                                </a>
                              );
                            })}
                            {noteRelations[note.NoteID].length > 3 && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-1">
                                +{noteRelations[note.NoteID].length - 3}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Tags - Compact */}
                      {note.Tags && note.Tags.trim() && (
                        <div className="mb-2">
                          <TagService
                            entityType="SalesNote"
                            entityId={note.NoteID}
                            initialTags={note.Tags ? (typeof note.Tags === 'string' ? note.Tags.split(',').map(t => t.trim()).filter(Boolean) : note.Tags) : []}
                            readonly={!hasEditRole}
                            maxTags={15}
                            onChange={async (newTags) => {
                              await fetchNotes();
                            }}
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Card Footer - Attachments indicator */}
                    {attachments[note.NoteID] && attachments[note.NoteID].length > 0 && (
                      <div className="px-4 pb-4">
                        <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-md">
                          <Paperclip className="w-3 h-3" />
                          <span>{attachments[note.NoteID].length} {attachments[note.NoteID].length === 1 ? safeT('Notes.attachment', 'attachment') : safeT('Notes.attachments', 'attachments')}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
                })}
              </div>
            )}
          </div>
        ) : (
          /* List View - Original Layout */
          <div className="space-y-4 sm:space-y-6">
            {loading && <p className="text-gray-600 dark:text-gray-400 text-sm">{safeT('Notes.loading', 'Loading...')}</p>}
            {error && <p className="text-red-500 dark:text-red-400 text-sm">{error}</p>}
            {filteredNotes.map(note => (
            <div 
              key={note.NoteID} 
              ref={el => noteRefs.current[note.NoteID] = el}
              className={`bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 ${
                focusedNoteId === note.NoteID ? 'ring-2 ring-yellow-400 ring-opacity-50' : ''
              }`}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-600 dark:text-blue-400 font-semibold text-xs sm:text-sm">
                      {note.FirstName ? note.FirstName.charAt(0) : 'U'}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-800 dark:text-gray-200 text-xs sm:text-sm truncate">
                      {`${note.FirstName} ${note.LastName}`}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {note.Type && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] sm:text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300">
                          {note.Type}
                        </span>
                      )}
                      <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                        {new Date(note.NoteDate).toLocaleDateString('th-TH', {
                          timeZone: 'Asia/Bangkok',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
                                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 hidden sm:block">
                      {new Date(note.NoteDate).toLocaleTimeString('th-TH', {
                        timeZone: 'Asia/Bangkok',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    <div className="flex space-x-0.5 sm:space-x-1">
                      {/* View Full Note Button */}
                      <button
                        onClick={() => handleNoteDoubleClick(note)}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        title={safeT('Notes.viewFullNote', 'View Full Note')}
                      >
                        <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                      
                      {/* Single Attachment Button - Always show, color changes based on attachment presence */}
                      <button
                        onClick={() => handleAttachmentModalOpen(note)}
                        className={`p-1 transition-colors ${
                          attachments[note.NoteID] && attachments[note.NoteID].length > 0
                            ? 'text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300'
                            : 'text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400'
                        }`}
                        title={
                          attachments[note.NoteID] && attachments[note.NoteID].length > 0
                            ? `${attachments[note.NoteID].length} ${attachments[note.NoteID].length === 1 ? safeT('Notes.attachment', 'attachment') : safeT('Notes.attachments', 'attachments')}`
                            : safeT('Notes.addAttachments', 'Add Attachments')
                        }
                      >
                        <Paperclip className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    
                    {/* AI Analysis Button - Under Construction */}
                    <button
                      onClick={() => handleAiAnalysis(note)}
                      className="p-1 transition-colors text-gray-400 dark:text-gray-500 hover:text-purple-600 dark:hover:text-purple-400"
                      title={safeT('Notes.aiAnalysis', 'AI Analysis')}
                    >
                      <Brain className="w-3 h-3 sm:w-4 sm:h-4" />
                    </button>
                    
                    {canEditNote(note) && (
                      <>
                        <button
                          onClick={() => handleEditNote(note)}
                          className="p-1 text-gray-400 dark:text-gray-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                          title={safeT('common.edit', 'Edit')}
                        >
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteNote(note.NoteID)}
                          disabled={deletingNote === note.NoteID}
                          className={`p-1 transition-colors ${
                            deletingNote === note.NoteID 
                              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                              : 'text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400'
                          }`}
                          title={safeT('common.delete', 'Delete')}
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <>
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 sm:p-4 rounded-md transition-colors duration-300">
                    <div className="text-gray-700 dark:text-gray-300 leading-relaxed break-words text-xs sm:text-sm md:text-base">
                      {isNoteLong(note.NoteText) && !expandedNotes[note.NoteID] ? (
                        <>
                          {renderNoteContent(truncateNote(note.NoteText))}
                          <span className="text-gray-400 dark:text-gray-500">...</span>
                        </>
                      ) : (
                        renderNoteContent(note.NoteText)
                      )}
                    </div>
                    {isNoteLong(note.NoteText) && (
                      <div className="mt-3 flex justify-center">
                        <button
                          onClick={() => toggleNoteExpansion(note.NoteID)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
                        >
                          {expandedNotes[note.NoteID] ? (
                            <>
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                              {safeT('Notes.showLess', safeT('common.showLess', 'Show Less'))}
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                              {safeT('Notes.showMore', safeT('common.showMore', 'Show More'))}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Related Entities Section per Note - Moved above Tags */}
                  {noteRelations[note.NoteID] && noteRelations[note.NoteID].length > 0 && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm text-gray-600 dark:text-gray-300">{safeT('Notes.relatedEntities', 'Relationships')}</div>
                        <button
                          onClick={() => toggleRelationsCollapse(note.NoteID)}
                          className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          {relationsCollapsed[note.NoteID] ? (safeT('common.show', 'Show')) : (safeT('common.hide', 'Hide'))}
                        </button>
                      </div>
                      {!relationsCollapsed[note.NoteID] && (
                        <div className="flex flex-wrap gap-2">
                          {noteRelations[note.NoteID].map((e, idx) => {
                            const colors = getEntityColors(e.type, e.customerType);
                            const displayName = e.type === 'Customer' && e.customerType === 'Person' && e.companyName
                              ? `${e.name || `#${e.id}`} (${e.companyName})`
                              : (e.name || `#${e.id}`);
                            return (
                              <a
                                key={`${e.type}-${e.id}-${idx}`}
                                href={getEntityRoute(e.type.toLowerCase(), e.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center space-x-2 px-2 py-1 text-xs border rounded transition-colors ${colors.border} ${colors.bg} ${colors.hoverBg}`}
                              >
                                <span className="flex items-center space-x-1">
                                  {getEntityIcon(e.type, 'h-3 w-3', e.customerType)}
                                  <span className={`font-medium ${colors.icon}`}>{e.type}</span>
                                </span>
                                <span className="text-gray-700 dark:text-gray-300 truncate max-w-[12rem]">
                                  {displayName}
                                </span>
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Tags Section */}
                  <div className="mt-4">
                    {note.Tags && note.Tags.trim() ? (
                      <TagService
                        entityType="SalesNote"
                        entityId={note.NoteID}
                        initialTags={note.Tags ? (typeof note.Tags === 'string' ? note.Tags.split(',').map(t => t.trim()).filter(Boolean) : note.Tags) : []}
                        readonly={!hasEditRole}
                        maxTags={15}
                        onChange={async (newTags) => {
                          // Refresh notes after tag change
                          await fetchNotes();
                        }}
                      />
                    ) : hasEditRole ? (
                      <TagService
                        entityType="SalesNote"
                        entityId={note.NoteID}
                        initialTags={[]}
                        readonly={false}
                        maxTags={15}
                        onChange={async (newTags) => {
                          // Refresh notes after tag change
                          await fetchNotes();
                        }}
                        placeholder={safeT('Notes.addTags', safeT('common.tags', 'Add tags...'))}
                      />
                    ) : null}
                  </div>
                  
                  {/* Attachments Section */}
                  <div className="mt-4">
                    {attachments[note.NoteID] && attachments[note.NoteID].length > 0 ? (
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <Paperclip className="w-3 h-3 text-gray-400" />
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {attachments[note.NoteID].length} {attachments[note.NoteID].length === 1 ? safeT('Notes.attachment', 'attachment') : safeT('Notes.attachments', 'attachments')}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                            <button
                              onClick={() => toggleAttachmentViewMode(note.NoteID, 'list')}
                              className={`p-1.5 rounded transition-colors ${
                                attachmentViewMode[note.NoteID] === 'list' || !attachmentViewMode[note.NoteID]
                                  ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                              }`}
                              title={safeT('Notes.listView', 'List View')}
                            >
                              <List className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => toggleAttachmentViewMode(note.NoteID, 'gallery')}
                              className={`p-1.5 rounded transition-colors ${
                                attachmentViewMode[note.NoteID] === 'gallery'
                                  ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                              }`}
                              title={safeT('Notes.galleryView', 'Gallery View')}
                            >
                              <Grid className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        {/* List View */}
                        {(attachmentViewMode[note.NoteID] === 'list' || !attachmentViewMode[note.NoteID]) && (
                          <div className="space-y-2">
                            {attachments[note.NoteID].map((attachment) => {
                              const attachmentTags = parseAttachmentTags(attachment.Tags);
                              return (
                                <div key={attachment.AttachmentID} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-md">
                                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                                    <div className="flex-shrink-0 text-gray-400">
                                      {getFileTypeIcon(attachment.MimeType, 'w-4 h-4')}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center">
                                        <button
                                          onClick={() => handleViewAttachment(attachment)}
                                          disabled={viewingAttachment}
                                          className="text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 transition-colors cursor-pointer text-left truncate"
                                          title={`${safeT('Notes.view', 'View')} ${decodeFileName(attachment.OriginalFileName || attachment.FileName)}`}
                                        >
                                          {decodeFileName(attachment.OriginalFileName || attachment.FileName)}
                                        </button>
                                        {attachment.TextContent && attachment.TextContent.trim() && (
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setViewingTextContent(attachment);
                                              setTextContentModalOpen(true);
                                            }}
                                            className="ml-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 flex-shrink-0"
                                            title={safeT('attachments.viewTextContent', 'View extracted text')}
                                          >
                                            <CheckCircle className="w-3 h-3" />
                                          </button>
                                        )}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {formatFileSize(attachment.FileSize, t)} • {attachment.MimeType}
                                      </div>
                                      {attachmentTags.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {attachmentTags.map((tag, idx) => (
                                            <span
                                              key={idx}
                                              className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-700"
                                            >
                                              {tag}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex space-x-1 flex-shrink-0 ml-2">
                                    <button
                                      onClick={() => handleViewAttachment(attachment)}
                                      disabled={viewingAttachment}
                                      className={`p-1 transition-colors ${
                                        viewingAttachment 
                                          ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                                          : 'text-gray-400 hover:text-green-600 dark:hover:text-green-400'
                                      }`}
                                      title={viewingAttachment 
                                        ? safeT('Notes.loadingAttachment', 'Loading attachment...') 
                                        : `${safeT('Notes.view', 'View')} ${decodeFileName(attachment.OriginalFileName || attachment.FileName)}`
                                      }
                                    >
                                      {viewingAttachment ? (
                                        <div className="w-4 h-4 border-2 border-gray-300 border-t-green-600 rounded-full animate-spin"></div>
                                      ) : (
                                        <Eye className="w-4 h-4" />
                                      )}
                                    </button>
                                    <button
                                      onClick={() => handleDownloadAttachment(attachment)}
                                      className="p-1 text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                                      title={`${safeT('Notes.download', 'Download')} ${decodeFileName(attachment.OriginalFileName || attachment.FileName)}`}
                                    >
                                      <Download className="w-4 h-4" />
                                    </button>
                                    {canEditNote(note) && (
                                      <button
                                        onClick={() => handleDeleteAttachment(attachment, note.NoteID)}
                                        disabled={deletingAttachments[attachment.AttachmentID]}
                                        className={`p-1 transition-colors ${
                                          deletingAttachments[attachment.AttachmentID]
                                            ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                            : 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300'
                                        }`}
                                        title={deletingAttachments[attachment.AttachmentID] 
                                          ? safeT('common.deleting', 'Deleting...') 
                                          : safeT('Notes.deleteAttachment', 'Delete Attachment')
                                        }
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Gallery View */}
                        {attachmentViewMode[note.NoteID] === 'gallery' && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {attachments[note.NoteID].map((attachment) => {
                              const attachmentTags = parseAttachmentTags(attachment.Tags);
                              const isImage = isImageFile(attachment.MimeType);
                              const fileName = decodeFileName(attachment.OriginalFileName || attachment.FileName);
                              const imageError = imageLoadErrors[attachment.AttachmentID];
                              const blobUrl = imageBlobUrls[attachment.AttachmentID];
                              const isLoading = imageLoading[attachment.AttachmentID];
                              
                              return (
                                <div
                                  key={attachment.AttachmentID}
                                  className="group relative bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200"
                                >
                                  {/* Image Preview or File Icon */}
                                  <div 
                                    className="relative aspect-square bg-gray-100 dark:bg-gray-800 flex items-center justify-center cursor-pointer overflow-hidden"
                                    onClick={() => handleViewAttachment(attachment)}
                                  >
                                    {isImage && blobUrl && !imageError ? (
                                      <img
                                        src={blobUrl}
                                        alt={fileName}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                        onError={() => {
                                          setImageLoadErrors(prev => ({ ...prev, [attachment.AttachmentID]: true }));
                                          // Clean up blob URL on error
                                          if (blobUrl) {
                                            window.URL.revokeObjectURL(blobUrl);
                                            setImageBlobUrls(prev => {
                                              const newState = { ...prev };
                                              delete newState[attachment.AttachmentID];
                                              return newState;
                                            });
                                          }
                                        }}
                                      />
                                    ) : isImage && isLoading && !imageError ? (
                                      <div className="flex flex-col items-center justify-center p-4 text-gray-400 h-full w-full">
                                        <div className="w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full animate-spin mb-2"></div>
                                        <span className="text-xs text-gray-500">Loading...</span>
                                      </div>
                                    ) : (
                                      <div className="flex flex-col items-center justify-center p-4 text-gray-400 h-full w-full">
                                        {getFileTypeIcon(attachment.MimeType, 'w-12 h-12 mb-2')}
                                        <span 
                                          className="text-xs text-center break-words px-2" 
                                          style={{ 
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden'
                                          }}
                                        >
                                          {fileName}
                                        </span>
                                      </div>
                                    )}
                                    
                                    {/* Overlay on hover */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                                      <div className="flex space-x-2">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleViewAttachment(attachment);
                                          }}
                                          disabled={viewingAttachment}
                                          className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                          title={safeT('Notes.view', 'View')}
                                        >
                                          <Eye className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDownloadAttachment(attachment);
                                          }}
                                          className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                          title={safeT('Notes.download', 'Download')}
                                        >
                                          <Download className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                                        </button>
                                        {canEditNote(note) && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteAttachment(attachment, note.NoteID);
                                            }}
                                            disabled={deletingAttachments[attachment.AttachmentID]}
                                            className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                                            title={safeT('Notes.deleteAttachment', 'Delete')}
                                          >
                                            <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* File Info */}
                                  <div className="p-2">
                                    <div className="flex items-center">
                                      <div 
                                        className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate cursor-pointer hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                                        onClick={() => handleViewAttachment(attachment)}
                                        title={fileName}
                                      >
                                        {fileName}
                                      </div>
                                      {attachment.TextContent && attachment.TextContent.trim() && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setViewingTextContent(attachment);
                                            setTextContentModalOpen(true);
                                          }}
                                          className="ml-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 flex-shrink-0"
                                          title={safeT('attachments.viewTextContent', 'View extracted text')}
                                        >
                                          <CheckCircle className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                      {formatFileSize(attachment.FileSize, t)}
                                    </div>
                                    
                                    {/* Tags */}
                                    {attachmentTags.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        {attachmentTags.slice(0, 3).map((tag, idx) => (
                                          <span
                                            key={idx}
                                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-700"
                                          >
                                            {tag}
                                          </span>
                                        ))}
                                        {attachmentTags.length > 3 && (
                                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400">
                                            +{attachmentTags.length - 3}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ) : (
                      <button
                        onClick={() => fetchAttachments(note.NoteID)}
                        className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 flex items-center"
                      >
                        <Paperclip className="w-3 h-3 mr-1" />
                        {safeT('Notes.viewAttachments', 'View attachments')}
                      </button>
                    )}
                  </div>
                  
                  {/* Analysis Results UI removed */}
                </>
            </div>
            ))}
            {!loading && filteredNotes.length === 0 && (
              <div className="text-center text-gray-500 dark:text-gray-400 text-sm">
                {searchTerm ? (
                  <div>
                    <p>{safeT('Notes.noSearchResults', 'No notes found matching your search.')}</p>
                    <button
                      onClick={() => setSearchTerm('')}
                      className="mt-2 text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 underline text-sm"
                    >
                      {safeT('Notes.clearSearch', 'Clear search')}
                    </button>
                  </div>
                ) : (
                  <p>{safeT('Notes.noNotes', 'No notes available')}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Attachment Upload Modal */}
      {showAttachmentModal && selectedNoteForAttachment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110]">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 transition-colors duration-300">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {safeT('Notes.addAttachments', 'Add Attachments')}
              </h2>
              <button
                onClick={handleAttachmentModalClose}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {safeT('Notes.addAttachmentsToNote', 'Add attachments to note:')} "{selectedNoteForAttachment.NoteText.substring(0, 50)}..."
              </p>
              
              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="attachment-files"
                  accept="*/*"
                />
                <label
                  htmlFor="attachment-files"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Paperclip className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {safeT('Notes.clickToSelectFiles', 'Click to select files')}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {safeT('Notes.maxFileSize', 'Max file size: 50MB')}
                  </span>
                </label>
              </div>

              {attachmentFiles.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {safeT('Notes.selectedFiles', 'Selected Files:')}
                  </h3>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {attachmentFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <div className="flex items-center space-x-2">
                          <Paperclip className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                            {decodeFileName(file.name)}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatFileSize(file.size, t)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleAttachmentModalClose}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                {safeT('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleAttachmentModalUpload}
                disabled={attachmentFiles.length === 0 || uploadingAttachments[selectedNoteForAttachment.NoteID]}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  attachmentFiles.length === 0 || uploadingAttachments[selectedNoteForAttachment.NoteID]
                    ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-teal-600 dark:bg-teal-500 text-white hover:bg-teal-700 dark:hover:bg-teal-600'
                }`}
              >
                {uploadingAttachments[selectedNoteForAttachment.NoteID] 
                  ? safeT('common.uploading', 'Uploading...') 
                  : safeT('common.upload', 'Upload')
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Attachment Modal */}
      {showViewAttachmentModal && selectedAttachmentForView && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4"
          onClick={handleCloseViewAttachment}
        >
          <div 
            className="relative modal-container bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            style={{ 
              width: modalSize.width, 
              height: modalSize.height,
              minWidth: '600px', 
              minHeight: '400px',
              maxWidth: '95vw',
              maxHeight: '95vh'
            }}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <Paperclip className="w-5 h-5 text-gray-500" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {decodeFileName(selectedAttachmentForView.OriginalFileName || selectedAttachmentForView.FileName)}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {formatFileSize(selectedAttachmentForView.FileSize, t)} • {selectedAttachmentForView.MimeType}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleDownloadAttachment(selectedAttachmentForView)}
                  className="p-2 text-gray-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                  title={safeT('Notes.downloadAttachment', 'Download Attachment')}
                >
                  <Download className="w-5 h-5" />
                </button>
                <button
                  onClick={handleCloseViewAttachment}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  title={safeT('common.close', 'Close')}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4 relative">
              {/* Resize Handle */}
              <div
                className="absolute bottom-2 right-2 w-6 h-6 cursor-se-resize opacity-50 hover:opacity-100 transition-opacity"
                onMouseDown={handleMouseDown}
                style={{
                  background: 'linear-gradient(135deg, transparent 50%, #9ca3af 50%)',
                  borderRadius: '0 0 4px 0'
                }}
                title={safeT('Notes.resize', 'Resize')}
              />
              {viewingAttachment ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-gray-500 dark:text-gray-400">
                    {safeT('Notes.loadingAttachment', 'Loading attachment...')}
                  </div>
                </div>
                             ) : attachmentViewUrl ? (
                 <div className="w-full h-full">
                   {(() => {
                     console.log('🔍 Rendering attachment content:', {
                       attachmentId: selectedAttachmentForView.AttachmentID,
                       mimeType: selectedAttachmentForView.MimeType,
                       fileName: selectedAttachmentForView.OriginalFileName || selectedAttachmentForView.FileName,
                       viewUrl: attachmentViewUrl
                     });
                     return null;
                   })()}
                   {/* Image Preview */}
                   {selectedAttachmentForView.MimeType && selectedAttachmentForView.MimeType.startsWith('image/') ? (
                     <div className="flex items-center justify-center w-full h-full">
                       {/* Try direct URL first for debugging */}
                       <img
                         src={`/api/attachments/view/${selectedAttachmentForView.AttachmentID}?t=${Date.now()}`}
                         alt={decodeFileName(selectedAttachmentForView.OriginalFileName || selectedAttachmentForView.FileName)}
                         className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                         style={{ maxHeight: 'calc(100vh - 200px)' }}
                         onLoad={(e) => {
                           console.log('🔍 Direct URL image loaded successfully:', {
                             attachmentId: selectedAttachmentForView.AttachmentID,
                             fileName: selectedAttachmentForView.OriginalFileName || selectedAttachmentForView.FileName,
                             naturalWidth: e.target.naturalWidth,
                             naturalHeight: e.target.naturalHeight,
                             currentSrc: e.target.currentSrc
                           });
                         }}
                         onError={(e) => {
                           console.error('🔍 Direct URL failed, trying blob URL:', {
                             attachmentId: selectedAttachmentForView.AttachmentID,
                             fileName: selectedAttachmentForView.OriginalFileName || selectedAttachmentForView.FileName,
                             mimeType: selectedAttachmentForView.MimeType,
                             error: e
                           });
                           
                           // Try blob URL as fallback
                           e.target.src = attachmentViewUrl;
                           
                           // Add error handler for blob URL
                           e.target.onerror = () => {
                             console.error('🔍 Both direct URL and blob URL failed');
                             setError(safeT('Notes.failedToLoadImage', 'Failed to load image'));
                           };
                         }}
                       />
                     </div>
                   ) : selectedAttachmentForView.MimeType && selectedAttachmentForView.MimeType === 'application/pdf' ? (
                     /* PDF Preview */
                     <div className="w-full h-full">
                       <iframe
                         src={attachmentViewUrl}
                         className="w-full h-full border-0 rounded-lg shadow-lg"
                         style={{ minHeight: 'calc(100vh - 200px)' }}
                         title={decodeFileName(selectedAttachmentForView.OriginalFileName || selectedAttachmentForView.FileName)}
                         onLoad={() => console.log('🔍 PDF loaded successfully:', selectedAttachmentForView.AttachmentID)}
                         onError={(e) => {
                           console.error('🔍 Failed to load PDF:', selectedAttachmentForView.AttachmentID, e);
                           setError(safeT('Notes.failedToLoadPDF', 'Failed to load PDF'));
                         }}
                       />
                     </div>
                   ) : selectedAttachmentForView.MimeType && (selectedAttachmentForView.MimeType.startsWith('text/') || selectedAttachmentForView.MimeType === 'application/json' || selectedAttachmentForView.MimeType === 'application/xml') ? (
                     /* Text File Preview */
                     <div className="w-full h-full">
                       <iframe
                         src={attachmentViewUrl}
                         className="w-full h-full border-0 rounded-lg shadow-lg"
                         style={{ minHeight: 'calc(100vh - 200px)' }}
                         title={decodeFileName(selectedAttachmentForView.OriginalFileName || selectedAttachmentForView.FileName)}
                         onLoad={() => console.log('🔍 Text file loaded successfully:', selectedAttachmentForView.AttachmentID)}
                         onError={(e) => {
                           console.error('🔍 Failed to load text file:', selectedAttachmentForView.AttachmentID, e);
                           setError(safeT('Notes.failedToLoadTextFile', 'Failed to load text file'));
                         }}
                       />
                     </div>
                   ) : selectedAttachmentForView.MimeType && (selectedAttachmentForView.MimeType.startsWith('audio/') || selectedAttachmentForView.MimeType.startsWith('video/')) ? (
                     /* Audio/Video Preview */
                     <div className="flex items-center justify-center h-full">
                       {selectedAttachmentForView.MimeType.startsWith('audio/') ? (
                         <audio controls className="max-w-full">
                           <source src={attachmentViewUrl} type={selectedAttachmentForView.MimeType} />
                           {safeT('Notes.audioNotSupported', 'Your browser does not support audio playback.')}
                         </audio>
                       ) : (
                         <video controls className="max-w-full max-h-full">
                           <source src={attachmentViewUrl} type={selectedAttachmentForView.MimeType} />
                           {safeT('Notes.videoNotSupported', 'Your browser does not support video playback.')}
                         </video>
                       )}
                     </div>
                   ) : (
                    /* Default Preview - Show file info and download button */
                    <div className="flex flex-col items-center justify-center h-64 space-y-4">
                      <div className="text-center">
                        <Paperclip className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                          {decodeFileName(selectedAttachmentForView.OriginalFileName || selectedAttachmentForView.FileName)}
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                          {formatFileSize(selectedAttachmentForView.FileSize, t)} • {selectedAttachmentForView.MimeType}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {safeT('Notes.previewNotAvailable', 'Preview not available for this file type. Please download to view.')}
                        </p>
                      </div>
                      <button
                        onClick={() => handleDownloadAttachment(selectedAttachmentForView)}
                        className="inline-flex items-center px-4 py-2 bg-teal-600 dark:bg-teal-500 text-white font-medium rounded-md hover:bg-teal-700 dark:hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        {safeT('Notes.downloadToView', 'Download to View')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-red-500 dark:text-red-400">
                    {safeT('Notes.failedToLoadAttachment', 'Failed to load attachment')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Text Content View Modal */}
      {textContentModalOpen && viewingTextContent && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-3xl rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {safeT('attachments.extractedText', 'Extracted Text Content')}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {viewingTextContent.OriginalFileName || viewingTextContent.FileName || 'Attachment'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setTextContentModalOpen(false);
                  setViewingTextContent(null);
                }}
                className="rounded-full bg-gray-100 p-2 text-gray-500 hover:text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-auto">
              {viewingTextContent.TextContent ? (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                  <pre className="text-sm font-mono whitespace-pre-wrap break-words text-gray-900 dark:text-gray-100">
                    {viewingTextContent.TextContent}
                  </pre>
                  <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                    {viewingTextContent.TextContent.length.toLocaleString()} characters
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500 dark:text-gray-400">
                    {safeT('attachments.noTextContent', 'No text content extracted yet.')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Note Modal */}
      {showEditModal && editingNote && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100] p-4"
          onClick={(e) => {
            // Prevent closing modal when clicking on overlay
            e.stopPropagation();
          }}
        >
          <div 
            className="relative bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {safeT('Notes.editNote', 'Edit Note')}
              </h2>
              <button
                onClick={handleCancelEdit}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title={safeT('common.cancel', 'Cancel')}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50 dark:bg-gray-900">
              {/* Type Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {safeT('Notes.noteType', 'Type')} / {safeT('Notes.noteTypeTh', 'ประเภท')}
                </label>
                <input
                  type="text"
                  value={editNoteType}
                  onChange={(e) => setEditNoteType(e.target.value)}
                  placeholder={safeT('Notes.noteTypePlaceholder', 'Enter note type...') || 'Enter note type...'}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-teal-500 transition-colors duration-300 text-sm"
                />
              </div>

              {/* Note Text */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {safeT('Notes.noteText', 'Note Text')}
                  </label>
                  <SpeechToText 
                    onTranscript={(transcript) => {
                      setEditNoteText(prev => prev ? prev + ' ' + transcript : transcript);
                    }}
                    language={i18n.language === 'en' ? 'en-US' : i18n.language === 'es' ? 'es-ES' : 'en-US'}
                  />
                </div>
                <textarea
                  value={editNoteText}
                  onChange={(e) => setEditNoteText(e.target.value)}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-teal-500 transition-colors duration-300"
                  rows="6"
                  placeholder={safeT('Notes.notePlaceholder', 'Enter note text...')}
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {safeT('common.tags', 'Tags')}
                </label>
                <TagService
                  entityType="SalesNote"
                  entityId={editingNote?.NoteID}
                  initialTags={editTags ? (typeof editTags === 'string' ? editTags.split(',').map(t => t.trim()).filter(Boolean) : editTags) : []}
                  maxTags={15}
                  onChange={(newTags) => {
                    setEditTags(newTags.join(', '));
                  }}
                  placeholder={safeT('Notes.tagsPlaceholder', 'Add tags...')}
                />
              </div>

              {/* Related Entities */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100">
                    {safeT('Notes.relatedEntities', 'Relationships')}
                  </h3>
                  {editingNoteRelatedEntities.length > 0 && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {editingNoteRelatedEntities.length} {editingNoteRelatedEntities.length === 1 ? 'entity' : 'entities'}
                    </span>
                  )}
                </div>
                
                {/* Entity Type Buttons */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['Customer','Lead','Opportunity','Activity'].map(type => {
                    const count = editingNoteRelatedEntities.filter(e => e.type === type).length;
                    const colors = getEntityColors(type);
                    const ringClass = ENTITY_RING_CLASSES[type] || ENTITY_RING_CLASSES.Customer;
                    const isSelected = count > 0;
                    const selectedClasses = isSelected ? `${ringClass} ring-2 ring-offset-1` : '';
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => openEntitySelectionForEditingNote(type)}
                        className={`group relative flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${colors.border} ${colors.bg} ${colors.hoverBorder} ${colors.hoverBg} ${selectedClasses}`}
                      >
                        <div className="mb-2">
                          {getEntityIcon(type, 'h-5 w-5')}
                        </div>
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          {safeT(`activities.entityTypes.${type.toLowerCase()}`, type)}
                        </span>
                        {isSelected && (
                          <span className={`absolute -top-2 -right-2 h-6 min-w-[1.5rem] px-1 rounded-full text-xs font-bold flex items-center justify-center ${colors.badge}`}>
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Selected Entities */}
                {editingNoteRelatedEntities.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {editingNoteRelatedEntities.map((e, idx) => {
                      const colors = getEntityColors(e.type, e.customerType);
                      const label = e.type === 'Customer' && e.customerType === 'Person' && e.companyName
                        ? `${e.name} (${e.companyName})`
                        : e.name;
                      return (
                        <div key={`${e.type}-${e.id}-${idx}`} className={`flex items-center space-x-2 px-3 py-1 border rounded ${colors.border} ${colors.bg}`}>
                          <div className="flex items-center space-x-1">
                            {getEntityIcon(e.type, 'h-3 w-3', e.customerType)}
                            <span className={`text-sm font-medium ${colors.icon}`}>{label}</span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-gray-400">#{e.id}</span>
                          <button 
                            type="button" 
                            onClick={() => setEditingNoteRelatedEntities(prev => mergeSortedCustomerOrder(prev.filter(x => !(x.type===e.type && x.id===e.id))))} 
                            className="text-red-500 hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Attachments Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100">
                    {safeT('Notes.attachments', 'Attachments')}
                  </h3>
                  {(editingNoteAttachments.length > 0 || editingNoteAttachmentFiles.length > 0) && (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {editingNoteAttachments.length + editingNoteAttachmentFiles.length} {(editingNoteAttachments.length + editingNoteAttachmentFiles.length) === 1 ? 'attachment' : 'attachments'}
                    </span>
                  )}
                </div>

                {/* Existing Attachments */}
                {editingNoteAttachments.length > 0 && (
                  <div className="space-y-2">
                    {editingNoteAttachments.map((attachment) => {
                      const attachmentTags = parseAttachmentTags(attachment.Tags);
                      const fileName = decodeFileName(attachment.OriginalFileName || attachment.FileName);
                      return (
                        <div key={attachment.AttachmentID} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600">
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <div className="flex-shrink-0 text-gray-400">
                              {getFileTypeIcon(attachment.MimeType, 'w-4 h-4')}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                                  {fileName}
                                </div>
                                {attachment.TextContent && attachment.TextContent.trim() && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setViewingTextContent(attachment);
                                      setTextContentModalOpen(true);
                                    }}
                                    className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                                    title={safeT('attachments.viewTextContent', 'View extracted text')}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {formatFileSize(attachment.FileSize)} • {attachment.MimeType}
                              </div>
                              {attachmentTags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {attachmentTags.slice(0, 3).map((tag, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-200 dark:border-teal-700"
                                    >
                                      {tag}
                                    </span>
                                  ))}
                                  {attachmentTags.length > 3 && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400">
                                      +{attachmentTags.length - 3}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-1 flex-shrink-0 ml-2">
                            <button
                              onClick={() => handleViewAttachment(attachment)}
                              disabled={viewingAttachment}
                              className="p-1 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                              title={safeT('Notes.view', 'View')}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDownloadAttachment(attachment)}
                              className="p-1 text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
                              title={safeT('Notes.download', 'Download')}
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            {editingNote && (() => {
                              const noteToEdit = notes.find(n => n.NoteID === editingNote);
                              return noteToEdit && canEditNote(noteToEdit);
                            })() && (
                              <button
                                onClick={async () => {
                                  if (window.confirm(safeT('Notes.confirmDeleteAttachment', `Are you sure you want to delete "${fileName}"?`))) {
                                    await handleDeleteAttachment(attachment, editingNote);
                                    // Refresh attachments list
                                    const tenantCode = getProtectedTenantCode();
                                    const response = await makeFetch(`/api/attachments/SalesNote/${editingNote}`, {
                                      headers: {
                                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                                        ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {})
                                      }
                                    });
                                    if (response.ok) {
                                      const data = await response.json();
                                      setEditingNoteAttachments(data.attachments || []);
                                    }
                                  }
                                }}
                                disabled={deletingAttachments[attachment.AttachmentID]}
                                className={`p-1 transition-colors ${
                                  deletingAttachments[attachment.AttachmentID]
                                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                                    : 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300'
                                }`}
                                title={safeT('Notes.deleteAttachment', 'Delete')}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* File Upload Area */}
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setEditingNoteAttachmentFiles(prev => [...prev, ...files]);
                      // Reset input to allow selecting the same file again
                      e.target.value = '';
                    }}
                    className="hidden"
                    id="edit-note-attachment-files"
                    accept="*/*"
                  />
                  <label
                    htmlFor="edit-note-attachment-files"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Paperclip className="w-8 h-8 text-gray-400 mb-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-400 text-center">
                      {safeT('Notes.clickToSelectFiles', 'Click to select files to upload')}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                      {safeT('Notes.maxFileSize', 'Max file size: 50MB')}
                    </span>
                  </label>
                </div>

                {/* Selected Files to Upload */}
                {editingNoteAttachmentFiles.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {safeT('Notes.filesToUpload', 'Files to upload')} ({editingNoteAttachmentFiles.length}):
                    </div>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {editingNoteAttachmentFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-teal-50 dark:bg-teal-900/20 rounded border border-teal-200 dark:border-teal-700">
                          <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <Paperclip className="w-4 h-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                            <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                              {decodeFileName(file.name)}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {formatFileSize(file.size, t)}
                            </span>
                            <button
                              onClick={() => {
                                setEditingNoteAttachmentFiles(prev => prev.filter((_, i) => i !== index));
                              }}
                              className="p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                              title={safeT('common.remove', 'Remove')}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer - Fixed */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <button
                onClick={handleCancelEdit}
                className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors font-medium"
              >
                {safeT('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={() => handleSaveEdit(editingNote)}
                className="px-6 py-2.5 bg-teal-600 dark:bg-teal-500 hover:bg-teal-700 dark:hover:bg-teal-600 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 transition-colors font-medium flex items-center shadow-sm"
              >
                <Check className="w-4 h-4 mr-2" />
                {safeT('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Suggestion Modal - Removed (Feature Under Construction) */}

      {/* AI Analysis Modal removed - feature under construction */}

      {/* Single Note View Modal - Full Screen */}
      {showNoteViewModal && selectedNoteForView && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="w-full h-full flex flex-col bg-white dark:bg-gray-900">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {safeT('Notes.viewNote', 'View Note')}
                </h2>
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300">
                  {selectedNoteForView.Type || 'Note'}
                </span>
              </div>
              
              {/* Header Actions */}
              <div className="flex items-center space-x-2">
                {canEditNote(selectedNoteForView) && (
                  <>
                    <button
                      onClick={() => {
                        handleEditNote(selectedNoteForView);
                      }}
                      className="p-2 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors"
                      title={safeT('common.edit', 'Edit')}
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    
                    <button
                      onClick={async () => {
                        if (window.confirm(safeT('Notes.confirmDelete', 'Are you sure you want to delete this note?') || 'Are you sure you want to delete this note?')) {
                          await handleDeleteNote(selectedNoteForView.NoteID);
                          setShowNoteViewModal(false);
                          setSelectedNoteForView(null);
                        }
                      }}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title={safeT('common.delete', 'Delete')}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </>
                )}
                
                <button
                  onClick={() => handleAiAnalysis(selectedNoteForView)}
                  className="p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                  title={safeT('Notes.aiAnalysis', 'AI Analysis')}
                >
                  <Brain className="w-5 h-5" />
                </button>
                
                <button
                  onClick={() => {
                    setShowNoteViewModal(false);
                    setSelectedNoteForView(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title={safeT('common.close', 'Close')}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Modal Content - Sidebar Layout */}
            <div className="flex-1 flex overflow-hidden">
              {/* Main Content Area */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Author and Date */}
                <div className="flex items-center space-x-4 mb-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 dark:text-blue-400 font-semibold text-2xl">
                      {selectedNoteForView.FirstName ? selectedNoteForView.FirstName.charAt(0) : 'U'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {`${selectedNoteForView.FirstName} ${selectedNoteForView.LastName}`}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(selectedNoteForView.NoteDate).toLocaleDateString('th-TH', {
                        timeZone: 'Asia/Bangkok',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                
                {/* Note Content */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {safeT('Notes.noteContent', 'Note Content')}
                    </h4>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setNoteContentFontSize(prev => Math.max(12, prev - 2))}
                        className="p-2 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-900/50 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
                        title={safeT('Notes.decreaseFontSize', 'Decrease font size')}
                        disabled={noteContentFontSize <= 12}
                      >
                        <ZoomOut className="w-5 h-5" />
                      </button>
                      <span className="text-xs text-gray-500 dark:text-gray-400 min-w-[3rem] text-center">
                        {noteContentFontSize}px
                      </span>
                      <button
                        onClick={() => setNoteContentFontSize(prev => Math.min(24, prev + 2))}
                        className="p-2 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 hover:bg-teal-200 dark:hover:bg-teal-900/50 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
                        title={safeT('Notes.increaseFontSize', 'Increase font size')}
                        disabled={noteContentFontSize >= 24}
                      >
                        <ZoomIn className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <div 
                      className="text-gray-900 dark:text-gray-100 leading-relaxed"
                      style={{ fontSize: `${noteContentFontSize}px`, lineHeight: '1.6' }}
                    >
                      {renderNoteContent(selectedNoteForView.NoteText)}
                    </div>
                  </div>
                </div>
                
                {/* Related Entities */}
                {noteRelations[selectedNoteForView.NoteID] && noteRelations[selectedNoteForView.NoteID].length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      {safeT('Notes.relatedEntities', 'Related Entities')}
                    </h4>
                    <div className="space-y-2">
                      {noteRelations[selectedNoteForView.NoteID].map((e, idx) => {
                        const colors = getEntityColors(e.type, e.customerType);
                        const displayName = e.type === 'Customer' && e.customerType === 'Person' && e.companyName
                          ? `${e.name || `#${e.id}`} (${e.companyName})`
                          : (e.name || `#${e.id}`);
                        return (
                          <a
                            key={`${e.type}-${e.id}-${idx}`}
                            href={getEntityRoute(e.type.toLowerCase(), e.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-teal-300 dark:hover:border-teal-600 transition-colors"
                          >
                            <div className="flex items-center space-x-3">
                              {getEntityIcon(e.type, 'h-5 w-5', e.customerType)}
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {displayName}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {e.type}
                                </p>
                              </div>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Sidebar - Note Metadata */}
              <div className="w-80 bg-gray-50 dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 p-6 overflow-y-auto">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {safeT('Notes.details', 'Details')}
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {safeT('Notes.noteId', 'Note ID')}
                    </label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                      {selectedNoteForView.NoteID}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {safeT('Notes.noteType', 'Type')}
                    </label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {selectedNoteForView.Type || 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {safeT('Notes.createdBy', 'Created By')}
                    </label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {`${selectedNoteForView.FirstName} ${selectedNoteForView.LastName}`}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {safeT('Notes.createdDate', 'Created Date')}
                    </label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {new Date(selectedNoteForView.NoteDate).toLocaleDateString('th-TH', {
                        timeZone: 'Asia/Bangkok',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </p>
                  </div>
                  
                  {selectedNoteForView.UpdatedDate && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {safeT('Notes.lastModified', 'Last Modified')}
                      </label>
                      <p className="mt-1 text-sm text-gray-900 dark:text-white">
                        {new Date(selectedNoteForView.UpdatedDate).toLocaleDateString('th-TH', {
                          timeZone: 'Asia/Bangkok',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {safeT('Notes.attachmentCount', 'Attachments')}
                    </label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {attachments[selectedNoteForView.NoteID]?.length || 0}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {safeT('Notes.relatedCount', 'Related Entities')}
                    </label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {noteRelations[selectedNoteForView.NoteID]?.length || 0}
                    </p>
                  </div>
                </div>
                
                {/* Tags Section */}
                {selectedNoteForView.Tags && selectedNoteForView.Tags.trim() && (
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                      {safeT('common.tags', 'Tags')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedNoteForView.Tags.split(',').map((tag, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                        >
                          {tag.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Attachments Section - Collapsible */}
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setShowAttachmentsInView(!showAttachmentsInView)}
                    className="w-full flex items-center justify-between mb-3 group"
                  >
                    <div className="flex items-center space-x-2">
                      <Paperclip className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                        {safeT('Notes.attachments', 'Attachments')} ({attachments[selectedNoteForView.NoteID]?.length || 0})
                      </h4>
                    </div>
                    <span className="text-gray-500 dark:text-gray-400 text-sm">
                      {showAttachmentsInView ? '▼' : '▶'}
                    </span>
                  </button>
                  
                  {showAttachmentsInView && (
                    <div className="space-y-2">
                      {attachments[selectedNoteForView.NoteID] && attachments[selectedNoteForView.NoteID].length > 0 ? (
                        attachments[selectedNoteForView.NoteID].map((attachment) => (
                          <div
                            key={attachment.AttachmentID}
                            className="p-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:border-teal-300 dark:hover:border-teal-600 transition-colors"
                          >
                            <div className="flex items-start space-x-2 mb-2">
                              <div className="flex-shrink-0 text-gray-400 dark:text-gray-500 mt-0.5">
                                {getFileTypeIcon(attachment.MimeType, 'w-4 h-4')}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                                    {attachment.FileName}
                                  </p>
                                  {attachment.TextContent && attachment.TextContent.trim() && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setViewingTextContent(attachment);
                                        setTextContentModalOpen(true);
                                      }}
                                      className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                                      title={safeT('attachments.viewTextContent', 'View extracted text')}
                                    >
                                      <CheckCircle className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {attachment.FileSize ? `${(attachment.FileSize / 1024).toFixed(2)} KB` : 'Unknown size'}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center space-x-1">
                              <button
                                onClick={() => handleViewAttachment(attachment)}
                                className="flex-1 px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                title={safeT('common.view', 'View')}
                              >
                                <Eye className="w-3 h-3 inline mr-1" />
                                View
                              </button>
                              <button
                                onClick={() => handleDownloadAttachment(attachment)}
                                className="flex-1 px-2 py-1 text-xs text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                                title={safeT('common.download', 'Download')}
                              >
                                <Download className="w-3 h-3 inline mr-1" />
                                Download
                              </button>
                              {canEditNote(selectedNoteForView) && (
                                <button
                                  onClick={() => handleDeleteAttachment(attachment.AttachmentID, selectedNoteForView.NoteID)}
                                  disabled={deletingAttachments[attachment.AttachmentID]}
                                  className="px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                  title={safeT('common.delete', 'Delete')}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                          {safeT('Notes.noAttachments', 'No attachments')}
                        </p>
                      )}
                      
                      {/* Add Attachment Button */}
                      {canEditNote(selectedNoteForView) && (
                        <button
                          onClick={() => {
                            setSelectedNoteForAttachment(selectedNoteForView);
                            setShowAttachmentModal(true);
                          }}
                          className="w-full mt-2 px-3 py-2 bg-teal-600 dark:bg-teal-500 text-white text-xs rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 transition-colors flex items-center justify-center space-x-1"
                        >
                          <Paperclip className="w-3 h-3" />
                          <span>{safeT('Notes.addAttachment', 'Add Attachment')}</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {showAddNoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {safeT('Notes.addNewNote', 'Add New Note')}
              </h3>
              <button
                onClick={() => {
                  setShowAddNoteModal(false);
                  setNewNote('');
                  setNoteType('Conversation');
                  setTags('');
                  setNewNoteRelatedEntities([]);
                  setNewNoteSelectedEntityTypes([]);
                  setNewNoteAttachmentFiles([]);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <form onSubmit={handleAddNote} className="space-y-4">
                {/* Type Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {safeT('Notes.noteType', 'Type')} / {safeT('Notes.noteTypeTh', 'ประเภท')}
                  </label>
                  <input
                    type="text"
                    value={noteType}
                    onChange={(e) => setNoteType(e.target.value)}
                    placeholder={safeT('Notes.noteTypePlaceholder', 'Enter note type...') || 'Enter note type...'}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-teal-500 transition-colors duration-300 text-sm"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {safeT('Notes.noteText', 'Note Content')}
                    </label>
                    <SpeechToText 
                      onTranscript={(transcript) => {
                        setNewNote(prev => prev ? prev + ' ' + transcript : transcript);
                      }}
                      language={i18n.language === 'en' ? 'en-US' : i18n.language === 'es' ? 'es-ES' : 'en-US'}
                    />
                  </div>
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder={safeT('Notes.notePlaceholder', 'Write your note here...')}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-teal-500 transition-colors duration-300 text-sm"
                    rows="6"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {safeT('common.tags', 'Tags')}
                  </label>
                  <TagService
                    initialTags={tags ? (typeof tags === 'string' ? tags.split(',').map(t => t.trim()).filter(Boolean) : tags) : []}
                    maxTags={15}
                    onChange={(newTags) => {
                      setTags(newTags.join(', '));
                    }}
                    placeholder={safeT('Notes.tagsPlaceholder', 'Add tags...')}
                  />
                </div>

                {/* Attachments Section */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-md font-medium text-gray-900 dark:text-gray-100">
                      {safeT('Notes.attachments', 'Attachments')}
                    </h3>
                    {newNoteAttachmentFiles.length > 0 && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {newNoteAttachmentFiles.length} {newNoteAttachmentFiles.length === 1 ? 'attachment' : 'attachments'}
                      </span>
                    )}
                  </div>

                  {/* File Upload Area */}
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setNewNoteAttachmentFiles(prev => [...prev, ...files]);
                        e.target.value = '';
                      }}
                      className="hidden"
                      id="new-note-attachment-files"
                      accept="*/*"
                    />
                    <label
                      htmlFor="new-note-attachment-files"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <Paperclip className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600 dark:text-gray-400 text-center">
                        {safeT('Notes.clickToSelectFiles', 'Click to select files to upload')}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        {safeT('Notes.maxFileSize', 'Max file size: 50MB')}
                      </span>
                    </label>
                  </div>

                  {/* Selected Files to Upload */}
                  {newNoteAttachmentFiles.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {safeT('Notes.filesToUpload', 'Files to upload')} ({newNoteAttachmentFiles.length}):
                      </div>
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {newNoteAttachmentFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-teal-50 dark:bg-teal-900/20 rounded border border-teal-200 dark:border-teal-700">
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <Paperclip className="w-4 h-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                              <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                {decodeFileName(file.name)}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatFileSize(file.size, t)}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setNewNoteAttachmentFiles(prev => prev.filter((_, i) => i !== index));
                                }}
                                className="p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                                title={safeT('common.remove', 'Remove')}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Related Entities for New Note */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-md font-medium text-gray-900 dark:text-gray-100">{safeT('Notes.relatedEntities', 'Relationships')}</h3>
                    {newNoteRelatedEntities.length > 0 && (
                      <span className="text-sm text-gray-500 dark:text-gray-400">{newNoteRelatedEntities.length} {newNoteRelatedEntities.length === 1 ? 'entity' : 'entities'}</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {['Customer','Lead','Opportunity','Activity'].map(type => {
                      const count = newNoteRelatedEntities.filter(e => e.type === type).length;
                      const colors = getEntityColors(type);
                      const ringClass = ENTITY_RING_CLASSES[type] || ENTITY_RING_CLASSES.Customer;
                      const isSelected = count > 0;
                      const selectedClasses = isSelected ? `${ringClass} ring-2 ring-offset-1` : '';
                      return (
                        <button
                          key={type}
                          type="button"
                          onClick={() => openEntitySelectionForNewNote(type)}
                          className={`group relative flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${colors.border} ${colors.bg} ${colors.hoverBorder} ${colors.hoverBg} ${selectedClasses}`}
                        >
                          <div className="mb-2">
                            {getEntityIcon(type, 'h-5 w-5')}
                          </div>
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{safeT(`activities.entityTypes.${type.toLowerCase()}`, type)}</span>
                          {isSelected && (
                            <span className={`absolute -top-2 -right-2 h-6 min-w-[1.5rem] px-1 rounded-full text-xs font-bold flex items-center justify-center ${colors.badge}`}>
                              {count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {newNoteRelatedEntities.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {newNoteRelatedEntities.map((e, idx) => {
                        const colors = getEntityColors(e.type, e.customerType);
                        const label = e.type === 'Customer' && e.customerType === 'Person' && e.companyName
                          ? `${e.name} (${e.companyName})`
                          : e.name;
                        return (
                          <div key={`${e.type}-${e.id}-${idx}`} className={`flex items-center space-x-2 px-3 py-1 border rounded ${colors.border} ${colors.bg}`}>
                            <div className="flex items-center space-x-1">
                              {getEntityIcon(e.type, 'h-3 w-3', e.customerType)}
                              <span className={`text-sm font-medium ${colors.icon}`}>{label}</span>
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">#{e.id}</span>
                            <button type="button" onClick={() => setNewNoteRelatedEntities(prev => mergeSortedCustomerOrder(prev.filter(x => !(x.type===e.type && x.id===e.id))))} className="text-red-500 hover:text-red-600">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="flex space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddNoteModal(false);
                      setNewNote('');
                      setNoteType('Conversation');
                      setTags('');
                      setNewNoteRelatedEntities([]);
                      setNewNoteSelectedEntityTypes([]);
                      setNewNoteAttachmentFiles([]);
                    }}
                    className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    {safeT('common.cancel', 'Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={!newNote.trim()}
                    className="flex-1 px-4 py-2 bg-teal-600 dark:bg-teal-500 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                  >
                    <PlusCircle className="w-5 h-5 mr-2" />
                    {safeT('Notes.addNote', 'Add Note')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create Summary Modal */}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-2 border-cyan-500 dark:border-teal-500">
            {/* Header - Cyan/Teal Gradient */}
            <div className="flex items-center justify-between p-6 border-b border-cyan-200 dark:border-teal-700 sticky top-0 bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-gray-800 dark:to-gray-800 z-10">
              <h3 className="text-lg font-semibold flex items-center">
                <Brain className="w-6 h-6 mr-2 text-cyan-600 dark:text-cyan-400" />
                <span className="bg-gradient-to-r from-cyan-600 to-teal-600 dark:from-cyan-400 dark:to-teal-400 bg-clip-text text-transparent">
                  {safeT('Notes.createSummary', 'Create AI Summary')} / {safeT('Notes.createSummaryTh', 'สร้างสรุปด้วย AI')}
                </span>
              </h3>
              <button
                onClick={handleCloseSummaryModal}
                disabled={generatingSummary}
                className="text-gray-400 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); handleGenerateSummary(); }} className="space-y-5">
                {/* Summary Style */}
                <div>
                  <label className="block text-sm font-medium bg-gradient-to-r from-cyan-700 to-teal-700 dark:from-cyan-400 dark:to-teal-400 bg-clip-text text-transparent mb-2">
                    {safeT('Notes.summaryStyle', 'Summary Style')} / {safeT('Notes.summaryStyleTh', 'รูปแบบสรุป')}
                  </label>
                  <select
                    value={summaryOptions.style}
                    onChange={(e) => setSummaryOptions(prev => ({ ...prev, style: e.target.value }))}
                    disabled={generatingSummary}
                    className="w-full p-3 border-2 border-cyan-200 dark:border-teal-700 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-300"
                  >
                    <option value="focus_points">Focus Points - จุดสำคัญและการดำเนินการ</option>
                    <option value="manager_report">Manager Report - รายงานผู้บริหาร</option>
                    <option value="sales_strategy">Sales Strategy - กลยุทธ์การขาย</option>
                    <option value="short_note">Short Note - สรุปสั้น</option>
                  </select>
                </div>

                {/* Language Selection */}
                <div>
                  <label className="block text-sm font-medium bg-gradient-to-r from-cyan-700 to-teal-700 dark:from-cyan-400 dark:to-teal-400 bg-clip-text text-transparent mb-2">
                    {safeT('Notes.summaryLanguage', 'Summary Language')} / {safeT('Notes.summaryLanguageTh', 'ภาษาของสรุป')}
                  </label>
                  <select
                    value={summaryOptions.language}
                    onChange={(e) => setSummaryOptions(prev => ({ ...prev, language: e.target.value }))}
                    disabled={generatingSummary}
                    className="w-full p-3 border-2 border-cyan-200 dark:border-teal-700 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-300"
                  >
                    <option value="th">🇹🇭 ภาษาไทย (Thai)</option>
                    <option value="en">🇬🇧 English</option>
                  </select>
                </div>

                {/* Scope */}
                <div>
                  <label className="block text-sm font-medium bg-gradient-to-r from-cyan-700 to-teal-700 dark:from-cyan-400 dark:to-teal-400 bg-clip-text text-transparent mb-2">
                    {safeT('Notes.summaryScope', 'Notes Scope')} / {safeT('Notes.summaryScopeTh', 'ขอบเขตบันทึก')}
                  </label>
                  <select
                    value={summaryOptions.scope}
                    onChange={(e) => setSummaryOptions(prev => ({ ...prev, scope: e.target.value }))}
                    disabled={generatingSummary}
                    className="w-full p-3 border-2 border-cyan-200 dark:border-teal-700 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-300"
                  >
                    <option value="my">My Notes Only / บันทึกของฉันเท่านั้น</option>
                    <option value="all">All Notes / บันทึกทั้งหมด</option>
                  </select>
                  
                  {/* Data Governance Info */}
                  {(() => {
                    const canViewAllNotes = selectedRole 
                      ? ['Manager', 'Admin'].includes(selectedRole)
                      : user?.roles?.some(role => 
                          role.RoleName === 'Manager' || 
                          role.RoleName === 'Admin'
                        );
                    
                    if (!canViewAllNotes && summaryOptions.scope === 'all') {
                      return (
                        <div className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                          <div className="flex items-start space-x-2">
                            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-yellow-800 dark:text-yellow-200">
                              <p className="font-semibold">Data Governance Policy Applied</p>
                              <p className="mt-1">
                                Based on your role <span className="font-semibold">({selectedRole || 'Salesperson'})</span>, you can only access your own notes. 
                                The summary will include only notes that you have permission to view (your notes only).
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    if (summaryOptions.scope === 'all' && canViewAllNotes) {
                      return (
                        <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                          <div className="flex items-start space-x-2">
                            <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-800 dark:text-blue-200">
                              <p>
                                Your role <span className="font-semibold">({selectedRole || 'Manager'})</span> allows you to view all notes. 
                                The summary will include notes from all users in your team.
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    return null;
                  })()}
                </div>

                {/* Note Type Filter - Compact Chip Style */}
                <div>
                  <label className="block text-sm font-medium bg-gradient-to-r from-cyan-700 to-teal-700 dark:from-cyan-400 dark:to-teal-400 bg-clip-text text-transparent mb-2">
                    {safeT('Notes.filterByNoteType', 'Filter by Note Type')} / {safeT('Notes.filterByNoteTypeTh', 'กรองตามประเภทบันทึก')} 
                    <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                      ({safeT('Notes.optional', 'Optional')} / {safeT('Notes.optionalTh', 'ไม่บังคับ')})
                    </span>
                  </label>
                  {availableNoteTypes.length > 0 ? (
                    <div className="border-2 border-cyan-200 dark:border-teal-700 rounded-lg bg-gradient-to-br from-cyan-50/50 to-teal-50/50 dark:from-gray-800/50 dark:to-gray-800/50 p-3">
                      {/* Compact Chip Layout */}
                      <div className="flex flex-wrap gap-2">
                        {availableNoteTypes.map((type) => {
                          const isSelected = summaryOptions.noteTypes.includes(type);
                          return (
                            <button
                              key={type}
                              type="button"
                              onClick={() => {
                                setSummaryOptions(prev => {
                                  const currentTypes = prev.noteTypes || [];
                                  if (isSelected) {
                                    return { ...prev, noteTypes: currentTypes.filter(t => t !== type) };
                                  } else {
                                    return { ...prev, noteTypes: [...currentTypes, type] };
                                  }
                                });
                              }}
                              disabled={generatingSummary}
                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                                isSelected
                                  ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-md ring-2 ring-cyan-300 dark:ring-cyan-600 transform scale-105'
                                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-cyan-400 dark:hover:border-cyan-500 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 hover:scale-105'
                              }`}
                            >
                              <span>📝</span>
                              <span>{type || safeT('Notes.noType', 'No Type')}</span>
                              {isSelected && (
                                <span className="ml-0.5 text-white font-bold">✓</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      
                      {/* Selected Count & Clear All */}
                      {summaryOptions.noteTypes && summaryOptions.noteTypes.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-cyan-200 dark:border-cyan-700/50 flex items-center justify-between">
                          <span className="text-xs font-medium text-cyan-700 dark:text-cyan-300">
                            ✅ {summaryOptions.noteTypes.length} {summaryOptions.noteTypes.length === 1 ? 'type' : 'types'} selected
                          </span>
                          <button
                            type="button"
                            onClick={() => setSummaryOptions(prev => ({ ...prev, noteTypes: [] }))}
                            className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                          >
                            {safeT('common.clearAll', 'Clear All')} / {safeT('common.clearAllTh', 'ล้างทั้งหมด')}
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-3 border-2 border-cyan-200 dark:border-teal-700 rounded-lg bg-gray-50 dark:bg-gray-800/50 text-center text-gray-500 dark:text-gray-400 text-sm">
                      {safeT('Notes.noNoteTypesAvailable', 'No note types available')} / {safeT('Notes.noNoteTypesAvailableTh', 'ไม่มีประเภทบันทึก')}
                    </div>
                  )}
                </div>

                {/* Filter By */}
                <div>
                  <label className="block text-sm font-medium bg-gradient-to-r from-cyan-700 to-teal-700 dark:from-cyan-400 dark:to-teal-400 bg-clip-text text-transparent mb-2">
                    {safeT('Notes.filterBy', 'Filter By')} / {safeT('Notes.filterByTh', 'กรองตาม')}
                  </label>
                  <select
                    value={summaryOptions.filterBy}
                    onChange={(e) => setSummaryOptions(prev => ({ ...prev, filterBy: e.target.value, filterValue: '' }))}
                    disabled={generatingSummary}
                    className="w-full p-3 border-2 border-cyan-200 dark:border-teal-700 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-300"
                  >
                    <option value="none">No Filter - All Notes / ไม่กรอง - ทั้งหมด</option>
                    <option value="tag">By Tag / ตามแท็ก</option>
                    <option value="date">By Date Range / ตามช่วงวันที่</option>
                    <option value="entity">By Related Entity / ตามหน่วยงานที่เกี่ยวข้อง</option>
                  </select>
                </div>

                {/* Filter Options */}
                {summaryOptions.filterBy === 'tag' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {safeT('Notes.selectTag', 'Select Tag')} / เลือกแท็ก
                      </label>
                      
                      {/* Toggle: Popular vs All Tags - Compact */}
                      <div className="flex gap-2 mb-3">
                        <button
                          type="button"
                          onClick={() => setTagFilterMode('popular')}
                          disabled={generatingSummary}
                          className={`flex-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            tagFilterMode === 'popular'
                              ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-md'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          🔥 Popular / ยอดนิยม
                        </button>
                        <button
                          type="button"
                          onClick={() => setTagFilterMode('all')}
                          disabled={generatingSummary}
                          className={`flex-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            tagFilterMode === 'all'
                              ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-md'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                          }`}
                        >
                          📋 All / ทั้งหมด
                        </button>
                      </div>

                      {/* Search Box - Compact */}
                      <input
                        type="text"
                        placeholder="🔍 Search tags... / ค้นหาแท็ก..."
                        value={tagSearchQuery}
                        onChange={(e) => setTagSearchQuery(e.target.value)}
                        disabled={generatingSummary}
                        className="w-full p-2 text-sm border-2 border-cyan-200 dark:border-teal-700 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-300 mb-3"
                      />

                      {/* Tag List - Compact Chip Style */}
                      <div className="border-2 border-cyan-200 dark:border-teal-700 rounded-lg bg-gradient-to-br from-cyan-50/50 to-teal-50/50 dark:from-gray-800/50 dark:to-gray-800/50 p-3 max-h-72 overflow-y-auto">
                        {(() => {
                          // Filter tags based on mode and search
                          const allTags = Object.entries(tagFrequencyMap);
                          const filteredTags = allTags
                            .filter(([tag, count]) => {
                              // Mode filter: popular = count >= 2
                              if (tagFilterMode === 'popular' && count < 2) return false;
                              // Search filter
                              if (tagSearchQuery && !tag.toLowerCase().includes(tagSearchQuery.toLowerCase())) return false;
                              return true;
                            })
                            .sort((a, b) => b[1] - a[1]); // Sort by frequency

                          if (filteredTags.length === 0) {
                            return (
                              <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
                                {tagSearchQuery ? (
                                  <>🔍 No tags found / ไม่พบแท็ก</>
                                ) : tagFilterMode === 'popular' ? (
                                  <>No popular tags (need 2+ notes) / ไม่มีแท็กยอดนิยม</>
                                ) : (
                                  <>No tags available / ไม่มีแท็ก</>
                                )}
                              </div>
                            );
                          }

                          return (
                            <div className="flex flex-wrap gap-2">
                              {filteredTags.map(([tag, count]) => {
                                const isSelected = summaryOptions.filterValue === tag;
                                return (
                                  <button
                                    key={tag}
                                    type="button"
                                    onClick={() => {
                                      setSummaryOptions(prev => ({ ...prev, filterValue: tag }));
                                    }}
                                    disabled={generatingSummary}
                                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                                      isSelected
                                        ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-md ring-2 ring-cyan-300 dark:ring-cyan-600 transform scale-105'
                                        : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-cyan-400 dark:hover:border-cyan-500 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 hover:scale-105'
                                    }`}
                                  >
                                    <span>🏷️</span>
                                    <span>{tag}</span>
                                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
                                      isSelected
                                        ? 'bg-white/30 text-white'
                                        : 'bg-cyan-100 dark:bg-cyan-900 text-cyan-600 dark:text-cyan-300'
                                    }`}>
                                      {count}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </div>

                      {summaryOptions.filterValue && (
                        <div className="mt-2 p-2 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-700 flex items-center justify-between">
                          <span className="text-xs font-medium text-cyan-700 dark:text-cyan-300">
                            ✅ Selected: <strong>{summaryOptions.filterValue}</strong>
                          </span>
                          <button
                            type="button"
                            onClick={() => setSummaryOptions(prev => ({ ...prev, filterValue: '' }))}
                            className="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                          >
                            Clear / ล้าง
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Date Range for Tag Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {safeT('Notes.dateRangeOptional', 'Date Range (Optional)')}
                      </label>
                      <select
                        value={summaryOptions.datePreset}
                        onChange={(e) => handleDatePresetChange(e.target.value)}
                        disabled={generatingSummary}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 transition-colors duration-300 mb-3"
                      >
                        <option value="">{safeT('Notes.allTime', 'All Time')}</option>
                        <option value="today">{safeT('Notes.today', 'Today')}</option>
                        <option value="thisweek">{safeT('Notes.thisWeek', 'This Week (Mon-Sun)')}</option>
                        <option value="lastweek">{safeT('Notes.lastWeek', 'Last Week (Mon-Sun)')}</option>
                        <option value="thismonth">{safeT('Notes.thisMonth', 'This Month')}</option>
                        <option value="lastmonth">{safeT('Notes.lastMonth', 'Last Month')}</option>
                        <option value="custom">{safeT('Notes.customRange', 'Custom Range')}</option>
                      </select>

                      {summaryOptions.datePreset === 'custom' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              {safeT('Notes.dateFrom', 'From Date')}
                            </label>
                            <input
                              type="date"
                              value={summaryOptions.dateFrom}
                              onChange={(e) => setSummaryOptions(prev => ({ ...prev, dateFrom: e.target.value }))}
                              disabled={generatingSummary}
                              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 transition-colors duration-300 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                              {safeT('Notes.dateTo', 'To Date')}
                            </label>
                            <input
                              type="date"
                              value={summaryOptions.dateTo}
                              onChange={(e) => setSummaryOptions(prev => ({ ...prev, dateTo: e.target.value }))}
                              disabled={generatingSummary}
                              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 transition-colors duration-300 text-sm"
                            />
                          </div>
                        </div>
                      )}

                      {summaryOptions.datePreset && summaryOptions.datePreset !== 'custom' && summaryOptions.dateFrom && summaryOptions.dateTo && (
                        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-2 rounded">
                          {safeT('Notes.selectedRange', 'Selected range')}: {summaryOptions.dateFrom} {safeT('common.to', 'to')} {summaryOptions.dateTo}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {summaryOptions.filterBy === 'date' && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {safeT('Notes.dateRangePreset', 'Date Range Preset')}
                      </label>
                      <select
                        value={summaryOptions.datePreset}
                        onChange={(e) => handleDatePresetChange(e.target.value)}
                        disabled={generatingSummary}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 transition-colors duration-300"
                      >
                        <option value="">{safeT('Notes.selectPreset', 'Select a preset...')}</option>
                        <option value="today">{safeT('Notes.today', 'Today')}</option>
                        <option value="thisweek">{safeT('Notes.thisWeek', 'This Week (Mon-Sun)')}</option>
                        <option value="lastweek">{safeT('Notes.lastWeek', 'Last Week (Mon-Sun)')}</option>
                        <option value="thismonth">{safeT('Notes.thisMonth', 'This Month')}</option>
                        <option value="lastmonth">{safeT('Notes.lastMonth', 'Last Month')}</option>
                        <option value="custom">{safeT('Notes.customRange', 'Custom Range')}</option>
                      </select>
                    </div>

                    {(summaryOptions.datePreset === 'custom' || !summaryOptions.datePreset) && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {safeT('Notes.dateFrom', 'From Date')}
                          </label>
                          <input
                            type="date"
                            value={summaryOptions.dateFrom}
                            onChange={(e) => setSummaryOptions(prev => ({ ...prev, dateFrom: e.target.value, datePreset: 'custom' }))}
                            disabled={generatingSummary}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 transition-colors duration-300"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {safeT('Notes.dateTo', 'To Date')}
                          </label>
                          <input
                            type="date"
                            value={summaryOptions.dateTo}
                            onChange={(e) => setSummaryOptions(prev => ({ ...prev, dateTo: e.target.value, datePreset: 'custom' }))}
                            disabled={generatingSummary}
                            className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 transition-colors duration-300"
                          />
                        </div>
                      </div>
                    )}

                    {summaryOptions.datePreset && summaryOptions.datePreset !== 'custom' && summaryOptions.dateFrom && summaryOptions.dateTo && (
                      <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-3 rounded">
                        <strong>{safeT('Notes.selectedRange', 'Selected range')}:</strong> {summaryOptions.dateFrom} {safeT('common.to', 'to')} {summaryOptions.dateTo}
                      </div>
                    )}
                  </div>
                )}

                {summaryOptions.filterBy === 'entity' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {safeT('Notes.selectEntityType', 'Entity Type')} / {safeT('Notes.selectEntityTypeTh', 'เลือกประเภทเอนทิตี้')}
                    </label>
                    <select
                      value={summaryOptions.entityType}
                      onChange={(e) => setSummaryOptions(prev => ({ ...prev, entityType: e.target.value, entityId: '', entityName: '' }))}
                      disabled={generatingSummary}
                      className="w-full p-3 border-2 border-cyan-300 dark:border-teal-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 transition-colors duration-300 mb-3"
                    >
                      <option value="">{safeT('Notes.selectEntityType', 'Select entity type...')}</option>
                      <option value="Customer">Customer / ลูกค้า</option>
                      <option value="Lead">Lead / โอกาส</option>
                      <option value="Opportunity">Opportunity / ดีล</option>
                      <option value="Activity">Activity / กิจกรรม</option>
                    </select>
                    {summaryOptions.entityType && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {safeT('Notes.selectEntity', 'Select Entity')} / {safeT('Notes.selectEntityTh', 'เลือกเอนทิตี้')}
                        </label>
                        {summaryOptions.entityId && summaryOptions.entityName ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 p-3 border-2 border-cyan-300 dark:border-teal-600 rounded-md bg-cyan-50 dark:bg-cyan-900/20 text-gray-900 dark:text-gray-100">
                              <div className="flex items-center gap-2">
                                {getEntityIcon(summaryOptions.entityType, 'w-5 h-5')}
                                <span className="font-medium">{summaryOptions.entityName}</span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">#{summaryOptions.entityId}</span>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => openEntitySelectionForSummary(summaryOptions.entityType)}
                              disabled={generatingSummary}
                              className="px-4 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 dark:from-cyan-600 dark:to-teal-600 dark:hover:from-cyan-700 dark:hover:to-teal-700 text-white rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                            >
                              {safeT('common.change', 'Change')}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openEntitySelectionForSummary(summaryOptions.entityType)}
                            disabled={generatingSummary}
                            className="w-full p-3 border-2 border-dashed border-cyan-300 dark:border-teal-600 rounded-md bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 hover:border-cyan-400 dark:hover:border-teal-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            <PlusCircle className="w-5 h-5" />
                            <span>{safeT('Notes.clickToSelectEntity', 'Click to select entity...')}</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Include Attachments Checkbox - Bottom Option - Only show if OCR is available */}
                {ocrAvailable && (
                  <div className="border-2 border-cyan-200 dark:border-teal-700 rounded-lg p-4 bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20">
                    <label className={`flex items-start space-x-3 ${!generatingSummary ? 'cursor-pointer' : 'cursor-not-allowed'} group`}>
                      <input
                        type="checkbox"
                        checked={summaryOptions.includeAttachments || false}
                        onChange={(e) => setSummaryOptions(prev => ({ ...prev, includeAttachments: e.target.checked }))}
                        disabled={generatingSummary}
                        className="mt-1 w-5 h-5 text-cyan-600 border-cyan-300 rounded focus:ring-cyan-500 focus:ring-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <div className="flex-1">
                        <div className="text-sm transition-colors text-gray-900 dark:text-gray-100 group-hover:text-cyan-700 dark:group-hover:text-cyan-300">
                          <Paperclip className="inline-block w-3 h-3 mr-1" />
                          {safeT('Notes.includeAttachments', 'Include attachment content (PDF, Word, Excel)')} / 
                          {safeT('Notes.includeAttachmentsTh', 'รวมเนื้อหาไฟล์แนบ (PDF, Word, Excel)')}
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                          {safeT('Notes.includeAttachmentsDescription', 'Attachments will be automatically summarized before inclusion in the note summary.')} / 
                          {safeT('Notes.includeAttachmentsDescriptionTh', 'ไฟล์แนบจะถูกสรุปโดยอัตโนมัติก่อนรวมเข้าในสรุปบันทึก')}
                        </p>
                      </div>
                    </label>
                  </div>
                )}

                {/* Additional Instructions */}
                <div className="border-2 border-cyan-200 dark:border-teal-700 rounded-lg p-4 bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20">
                  <label className="block text-sm font-medium bg-gradient-to-r from-cyan-700 to-teal-700 dark:from-cyan-400 dark:to-teal-400 bg-clip-text text-transparent mb-2">
                    {safeT('Notes.additionalInstructions', 'Additional Instructions')} / {safeT('Notes.additionalInstructionsTh', 'สิ่งที่ต้องการให้ทำเพิ่มเติม')}
                  </label>
                  <textarea
                    value={summaryOptions.additionalInstructions || ''}
                    onChange={(e) => setSummaryOptions(prev => ({ ...prev, additionalInstructions: e.target.value }))}
                    disabled={generatingSummary}
                    placeholder={
                      summaryOptions.language === 'th' 
                        ? 'ตัวอย่าง: โปรดเน้นที่ลูกค้าที่มีโอกาสสูง หรือ ระบุแนวโน้มที่สำคัญ...' 
                        : 'Example: Please focus on high-opportunity customers, or identify key trends...'
                    }
                    rows={4}
                    className="w-full p-3 border-2 border-cyan-200 dark:border-teal-700 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors duration-300 resize-y"
                  />
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                    {safeT('Notes.additionalInstructionsDescription', 'These instructions will be added to the AI prompt to customize the summary output.')} / 
                    {safeT('Notes.additionalInstructionsDescriptionTh', 'คำสั่งเหล่านี้จะถูกเพิ่มเข้าในคำสั่ง AI เพื่อปรับแต่งผลลัพธ์ของสรุป')}
                  </p>
                </div>

                {/* Progress Indicator */}
                {generatingSummary && summaryProgress.message && (
                  <div className="bg-gradient-to-r from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 border-2 border-cyan-200 dark:border-teal-700 rounded-lg p-4 shadow-md">
                    <div className="flex items-center mb-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-600 dark:border-cyan-400 mr-3"></div>
                      <span className="text-sm font-medium bg-gradient-to-r from-cyan-700 to-teal-700 dark:from-cyan-300 dark:to-teal-300 bg-clip-text text-transparent">
                        {summaryProgress.message}
                      </span>
                    </div>
                    {summaryProgress.total > 0 && (
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-cyan-500 to-teal-500 dark:from-cyan-400 dark:to-teal-400 h-2 rounded-full transition-all duration-300 shadow-sm"
                          style={{ width: `${(summaryProgress.current / summaryProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4 border-t-2 border-cyan-200 dark:border-teal-700">
                  <button
                    type="button"
                    onClick={handleCloseSummaryModal}
                    disabled={generatingSummary}
                    className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {safeT('common.cancel', 'Cancel')} / {safeT('common.cancelTh', 'ยกเลิก')}
                  </button>
                  <button
                    type="submit"
                    disabled={generatingSummary || (summaryOptions.filterBy === 'tag' && !summaryOptions.filterValue) || (summaryOptions.filterBy === 'entity' && (!summaryOptions.entityType || !summaryOptions.entityId))}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 dark:from-cyan-600 dark:to-teal-600 dark:hover:from-cyan-700 dark:hover:to-teal-700 text-white rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 disabled:transform-none flex items-center justify-center font-semibold"
                  >
                    <Brain className="w-5 h-5 mr-2" />
                    {generatingSummary ? safeT('Notes.generating', 'Generating...') : `${safeT('Notes.generateSummary', 'Generate Summary')} / ${safeT('Notes.generateSummaryTh', 'สร้างสรุป')}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <EntitySelectionModal
        isOpen={entitySelectionModal.isOpen}
        entityType={entitySelectionModal.entityType}
        title={`${safeT('Notes.select', 'Select')} ${safeT(`activities.entityTypes.${entitySelectionModal.entityType?.toLowerCase()}`, entitySelectionModal.entityType || '')}`.trim()}
        subtitle={entitySelectionModal.selectedIds.length > 0 ? `${entitySelectionModal.selectedIds.length} ${safeT('common.selected', 'selected')}` : undefined}
        searchTerm={entitySelectionModal.searchTerm}
        results={entitySelectionModal.results}
        selectedIds={entitySelectionModal.selectedIds}
        loading={entitySelectionModal.loading}
        searchPlaceholder={safeT('Notes.searchEntity', safeT('common.search', 'Search'))}
        loadingMessage={safeT('Notes.searching', safeT('common.searching', 'Searching...'))}
        emptyMessage={safeT('Notes.noResultsFound', safeT('common.noResultsFound', 'No results found'))}
        idleMessage={safeT('Notes.startTypingToSearch', safeT('common.startTypingToSearch', 'Start typing to search'))}
        cancelLabel={safeT('common.cancel', 'Cancel')}
        confirmLabel={safeT('common.save', 'Save')}
        onCancel={() => closeEntitySelectionModal(false)}
        onConfirm={() => closeEntitySelectionModal(true)}
        onSearchTermChange={handleEntityModalSearchChange}
        onToggle={toggleEntitySelectionInModal}
        getEntityId={getEntityId}
        getEntityName={getEntityName}
        t={t}
      />
    </div>
  );
};

export default Notes; 